import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { adminApi, requestTypeSettingsApi, profileApi, irbApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { EmailHealthCard } from '../components/EmailHealthCard';
import { 
  Settings, 
  Clock, 
  Mail, 
  Eye, 
  Send, 
  Edit, 
  MessageSquare, 
  FileText,
  Lock,
  Bell,
  BellRing,
  ArrowUpRight,
  RotateCcw,
  Play,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  GripVertical,
  Type,
  Calendar,
  CheckSquare,
  Upload,
  Download,
  Info,
  List,
  Users,
  X,
  ShieldCheck,
  LayoutGrid,
  FileStack
} from 'lucide-react';
import { OverviewLayoutEditor } from '../components/admin/OverviewLayoutEditor';
import { BundleToolConfig } from '../components/admin/BundleToolConfig';
import { SignatureRoundsConfig } from '../components/admin/SignatureRoundsConfig';
import { RoleVisibilityConfig } from '../components/admin/RoleVisibilityConfig';

// Sortable Field Item Component for drag-and-drop
function IRBDocRow({ doc, onDelete, onSaveDescription }) {
  const [desc, setDesc] = useState(doc.description || '');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onSaveDescription(desc);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="p-3 border rounded-lg hover:bg-slate-50"
      data-testid={`irb-admin-doc-${doc.id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
          <span className="text-sm font-medium text-slate-900 truncate">{doc.filename}</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditing((v) => !v)}
            data-testid={`irb-edit-desc-${doc.id}`}
            className="text-slate-600 hover:text-slate-800"
          >
            {editing ? 'Cancel' : (doc.description ? 'Edit note' : 'Add note')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            data-testid={`irb-delete-${doc.id}`}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {!editing && doc.description && (
        <p
          className="text-xs text-slate-600 mt-2 pl-8 leading-snug"
          data-testid={`irb-admin-doc-desc-${doc.id}`}
        >
          {doc.description}
        </p>
      )}
      {editing && (
        <div className="mt-2 pl-8 space-y-2">
          <Textarea
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="Short note shown below the file (e.g., Use for Phase II protocols only)"
            data-testid={`irb-desc-input-${doc.id}`}
          />
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              onClick={save}
              disabled={saving}
              data-testid={`irb-save-desc-${doc.id}`}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {saving ? 'Saving…' : 'Save note'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function SortableFieldItem({ field, index, onEdit, onDelete, fieldTypes }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: field.id,
    transition: { duration: 250, easing: 'cubic-bezier(0.25, 1, 0.5, 1)' },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    position: 'relative',
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-4 border rounded-lg bg-white hover:border-slate-300 ${isDragging ? 'shadow-lg' : ''}`}
      data-testid={`custom-field-${field.id}`}
    >
      <div className="flex items-center gap-4">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded"
        >
          <GripVertical className="w-5 h-5 text-slate-400" />
        </button>
        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-medium text-slate-600">
          {index + 1}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-900">{field.label}</span>
            <Badge variant="outline" className="text-xs">
              {fieldTypes.find(t => t.value === field.type)?.label || field.type}
            </Badge>
            {field.required && (
              <Badge className="bg-amber-100 text-amber-700 text-xs">Required</Badge>
            )}
            {!field.enabled && (
              <Badge variant="outline" className="text-slate-500 text-xs">Disabled</Badge>
            )}
            {field.conditions?.length > 0 && (
              <Badge className="bg-purple-100 text-purple-700 text-xs">Conditional</Badge>
            )}
          </div>
          {field.help_text && (
            <p className="text-xs text-slate-500 mt-0.5">{field.help_text}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => onEdit(field)}>
          <Edit className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={() => onDelete(field.id)}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// Unified Sortable Field Item (for both standard and custom fields)
function UnifiedSortableField({ field, index, isStandard, onEditStandard, onEditCustom, onDeleteCustom, fieldTypes }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: field.id,
    transition: { duration: 250, easing: 'cubic-bezier(0.25, 1, 0.5, 1)' },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    position: 'relative',
  };

  const getBgColor = () => {
    if (isStandard) return 'bg-blue-50 border-blue-200';
    return 'bg-white border-slate-200';
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 border rounded-lg ${getBgColor()} hover:shadow-sm ${isDragging ? 'shadow-lg z-10' : ''}`}
      data-testid={`unified-field-${field.id}`}
    >
      <div className="flex items-center gap-3">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-white/50 rounded"
        >
          <GripVertical className="w-5 h-5 text-slate-400" />
        </button>
        <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center text-sm font-medium text-slate-600 border">
          {index + 1}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-900">{field.label}</span>
            {isStandard ? (
              <Badge className="bg-blue-100 text-blue-700 text-xs">Standard</Badge>
            ) : (
              <Badge variant="outline" className="text-xs">
                {fieldTypes?.find(t => t.value === field.type)?.label || field.type}
              </Badge>
            )}
            {field.required && (
              <Badge className="bg-amber-100 text-amber-700 text-xs">Required</Badge>
            )}
            {field.enabled === false && (
              <Badge variant="outline" className="text-slate-400 text-xs">Hidden</Badge>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {isStandard ? (
          <Button variant="ghost" size="sm" onClick={() => onEditStandard(field.id)}>
            <Settings className="w-4 h-4" />
          </Button>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={() => onEditCustom(field)}>
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => onDeleteCustom(field.id)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// Sortable Form Card Component for the new UI
function SortableFormCard({ fieldKey, fieldInfo, fieldConfig, customField, index, isCustom, isExpanded, onToggleExpand, onConfigChange, onEditCustomField, onDeleteCustomField, FIELD_TYPE_ICONS }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: fieldKey,
    transition: {
      duration: 250,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
    },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 50 : 'auto',
    position: 'relative',
  };

  const isAlwaysEnabled = fieldInfo?.alwaysEnabled;
  const isEnabled = isCustom ? customField?.enabled !== false : fieldConfig?.enabled !== false;
  const isRequired = isCustom ? customField?.required : fieldConfig?.required;
  const fieldLabel = isCustom ? customField?.label : (fieldConfig?.label || fieldInfo?.defaultLabel);
  const fieldType = isCustom ? customField?.type : 'standard';

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg overflow-hidden transition-all ${
        !isEnabled && !isAlwaysEnabled ? 'bg-slate-50 opacity-60' : 'bg-white'
      } ${isDragging ? 'shadow-xl ring-2 ring-blue-400' : ''}`}
      data-testid={`form-field-${fieldKey}`}
    >
      {/* Collapsed Header */}
      <div 
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50"
        onClick={() => onToggleExpand(fieldKey)}
      >
        <div className="flex items-center gap-3">
          {/* Drag Handle */}
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-200 rounded text-slate-400 hover:text-slate-600"
            style={{ touchAction: 'none' }}
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="w-4 h-4" />
          </button>
          
          {/* Position number */}
          <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-600">
            {index + 1}
          </div>
          
          {/* Field Icon & Name */}
          <div className="flex items-center gap-2">
            <span className="text-base">{isCustom ? (FIELD_TYPE_ICONS?.[fieldType] || '📝') : '📄'}</span>
            <span className="font-medium text-slate-900">{fieldLabel}</span>
          </div>
          
          {/* Badges */}
          <div className="flex items-center gap-1.5">
            {isAlwaysEnabled && (
              <Badge variant="outline" className="text-[10px] bg-slate-100 px-1.5 py-0">
                <Lock className="w-2.5 h-2.5 mr-0.5" />
                Core
              </Badge>
            )}
            {isCustom && (
              <Badge className="text-[10px] bg-violet-100 text-violet-700 px-1.5 py-0">
                Custom
              </Badge>
            )}
            {isRequired && (
              <Badge className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0">
                Required
              </Badge>
            )}
            {!isEnabled && !isAlwaysEnabled && (
              <Badge className="text-[10px] bg-slate-200 text-slate-500 px-1.5 py-0">
                Hidden
              </Badge>
            )}
          </div>
        </div>
        
        {/* Right Side Controls */}
        <div className="flex items-center gap-3">
          {!isAlwaysEnabled && !isCustom && (
            <Switch
              checked={isEnabled}
              onCheckedChange={(checked) => {
                onConfigChange(fieldKey, 'enabled', checked);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          {!isAlwaysEnabled && !isCustom && (
            <button
              type="button"
              className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
              title="Hide this field (soft-delete; restore from Hidden Fields below)"
              onClick={(e) => { e.stopPropagation(); onConfigChange(fieldKey, '_deleted', true); }}
              data-testid={`hide-core-field-${fieldKey}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
        </div>
      </div>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-2 border-t bg-slate-50/50">
          <p className="text-xs text-slate-500 mb-3">{fieldInfo?.description || customField?.help_text || 'Configure this field'}</p>
          
          {isCustom ? (
            // Custom field expanded view
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Type: <span className="font-medium">{FIELD_TYPE_ICONS?.[fieldType]} {fieldType}</span></span>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); onEditCustomField?.(customField); }}
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={(e) => { e.stopPropagation(); onDeleteCustomField?.(customField?.id); }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              {customField?.placeholder && (
                <p className="text-xs text-slate-500">Placeholder: "{customField.placeholder}"</p>
              )}
            </div>
          ) : (
            // Standard field expanded view
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Custom Label</Label>
                <Input
                  value={fieldConfig?.label || ''}
                  onChange={(e) => onConfigChange(fieldKey, 'label', e.target.value)}
                  placeholder={fieldInfo?.defaultLabel}
                  className="mt-1 h-8 text-sm"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Placeholder</Label>
                <Input
                  value={fieldConfig?.placeholder || ''}
                  onChange={(e) => onConfigChange(fieldKey, 'placeholder', e.target.value)}
                  placeholder="Enter placeholder..."
                  className="mt-1 h-8 text-sm"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-slate-600">Help Text</Label>
                <Input
                  value={fieldConfig?.help_text || ''}
                  onChange={(e) => onConfigChange(fieldKey, 'help_text', e.target.value)}
                  placeholder="Optional help text..."
                  className="mt-1 h-8 text-sm"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="col-span-2 flex items-center gap-4 pt-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={fieldConfig?.required === true}
                    onCheckedChange={(checked) => onConfigChange(fieldKey, 'required', checked)}
                    disabled={isAlwaysEnabled && fieldConfig?.required}
                  />
                  <Label className="text-xs text-slate-600">Required field</Label>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Privilege settings labels
const PRIVILEGE_LABELS = {
  admin: { label: 'Admin', locked: true, description: 'System administrators (always enabled)' },
  doc_thread_creator: { label: 'Doc Thread Creator', locked: false, description: 'User who created the document thread' },
  chairman: { label: 'Chairman', locked: true, description: 'Chairman role (always enabled)' },
  coordinator: { label: 'Coordinator (Assigned to Request)', locked: false, description: 'Coordinator assigned to the specific request' },
  crd: { label: 'CRD', locked: true, description: 'Clinical Research Department role (always enabled)' },
  doc_thread_assignees: { label: 'Doc Thread Assignees', locked: false, description: 'Users assigned to review the document' },
  pi: { label: 'Principal Investigator', locked: false, description: 'PI role' },
  reviewer: { label: 'Reviewer', locked: false, description: 'Reviewer role' },
  company_rep: { label: 'Company Rep', locked: false, description: 'Company Representative role' },
};

export default function AdminConfig() {
  const [activeRequestType, setActiveRequestType] = useState('FEA');
  // Upload dialog doc types (per request type, admin-configurable)
  const [uploadDocTypes, setUploadDocTypes] = useState([]);
  const [uploadDocTypeDefaults, setUploadDocTypeDefaults] = useState([]);
  const [newUploadDocType, setNewUploadDocType] = useState('');
  const [uploadDocTypesSaving, setUploadDocTypesSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  // Data states
  const [reminderPolicies, setReminderPolicies] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailCategories, setEmailCategories] = useState([]);
  const [discussionSettings, setDiscussionSettings] = useState({ FEA: {}, CTA: {} });
  // iter167 Issue 35 — Per-study instance cap: {mode: 'unlimited'|'once'|'fixed', max_count: N}
  const [perStudyLimits, setPerStudyLimits] = useState({
    FEA: { mode: 'unlimited', max_count: 1 },
    CTA: { mode: 'unlimited', max_count: 1 },
    FT:  { mode: 'unlimited', max_count: 1 },
    IRB: { mode: 'unlimited', max_count: 1 },
  });
  const [savingPerStudyLimit, setSavingPerStudyLimit] = useState(false);
  const [formConfigs, setFormConfigs] = useState({ FEA: null, CTA: null, FT: null, FT_PATH_A: null, FT_PATH_B: null });
  const [ftFormPath, setFtFormPath] = useState('A'); // 'A' or 'B' — selects which FT form to edit
  const [customFields, setCustomFields] = useState({ FEA: { fields: [], field_order: [] }, CTA: { fields: [], field_order: [] }, FT: { fields: [], field_order: [] }, FT_PATH_A: { fields: [], field_order: [] }, FT_PATH_B: { fields: [], field_order: [] } });
  const [unifiedFieldOrder, setUnifiedFieldOrder] = useState({ FEA: null, CTA: null, FT: null });  // Unified order of all fields
  const [notificationConfigs, setNotificationConfigs] = useState({ FEA: null, CTA: null });
  // IRB resources (read-only library)
  const [irbInfo, setIrbInfo] = useState('');
  const [irbDocs, setIrbDocs] = useState([]);
  const [irbLoading, setIrbLoading] = useState(false);
  const [irbInfoSaving, setIrbInfoSaving] = useState(false);
  const [irbUploading, setIrbUploading] = useState(false);
  const [roleDocConfigs, setRoleDocConfigs] = useState([]);
  const [editingRoleDoc, setEditingRoleDoc] = useState(null);
  const [savingRoleDoc, setSavingRoleDoc] = useState(false);
  // Role-doc add/edit modal state
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [docModalRole, setDocModalRole] = useState(null);
  const [docModalIdx, setDocModalIdx] = useState(null);  // null = new, number = editing
  const [docModalForm, setDocModalForm] = useState({ name: '', has_expiry: false, enforce: 'warning', notify_days_before: 30 });
  const [expandedDocNotif, setExpandedDocNotif] = useState({});
  const [docNotifConfigs, setDocNotifConfigs] = useState({});
  
  const DOC_NOTIFICATION_EVENTS = [
    { key: 'doc_expiry_warning', label: 'Document Expiry Warning', desc: 'Sent when a required document is approaching its expiry date', defaultChannel: 'both',
      defaultRecipients: ['USER', 'ADMIN', 'COORDINATOR', 'CHAIRMAN', 'CRD'],
      templateSubject: '{user_name}\'s {doc_type} expires in {days_until} days' },
    { key: 'doc_expired', label: 'Document Expired', desc: 'Sent when a required document has expired', defaultChannel: 'both',
      defaultRecipients: ['USER', 'ADMIN', 'COORDINATOR', 'CHAIRMAN', 'CRD'],
      templateSubject: '{user_name}\'s {doc_type} has expired' },
    { key: 'doc_missing_assignment', label: 'Assignment Blocked', desc: 'Sent when assignment is blocked due to missing documents', defaultChannel: 'in_app',
      defaultRecipients: ['USER', 'ADMIN'],
      templateSubject: 'Assignment blocked: {user_name} is missing required documents' },
    { key: 'doc_urgent_request', label: 'Urgent Document Request', desc: 'Sent manually by staff from the assignee documents modal — asks the user to upload/refresh a specific required document. Rate-limited to once per 24h per (user, doc).', defaultChannel: 'email',
      defaultRecipients: ['USER'],
      templateSubject: 'ACTION REQUIRED: Please upload your {doc_type} for {ref_no}' },
  ];
  
  // Modal states
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [showFormConfigModal, setShowFormConfigModal] = useState(false);
  const [showCustomFieldModal, setShowCustomFieldModal] = useState(false);
  const [showNotificationTestModal, setShowNotificationTestModal] = useState(false);
  
  // Form states
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [policyForm, setPolicyForm] = useState({
    enabled: true,
    send_email: true,
    send_push: true,
    initial_days: 3,
    reminder_days: 2,
    max_reminders: 3,
    escalation_enabled: true,
    escalation_after: 3
  });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ subject: '', body: '', email_enabled: true, push_enabled: true });
  const [previewHtml, setPreviewHtml] = useState('');
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [activeEmailCategory, setActiveEmailCategory] = useState(null);
  const [savingFormConfig, setSavingFormConfig] = useState(false);
  const [editingCustomField, setEditingCustomField] = useState(null);
  const [savingCustomField, setSavingCustomField] = useState(false);
  const [savingNotification, setSavingNotification] = useState(null);
  const [testingNotification, setTestingNotification] = useState(null);
  const [customFieldForm, setCustomFieldForm] = useState({
    id: '',
    type: 'text',
    label: '',
    placeholder: '',
    help_text: '',
    required: false,
    enabled: true,
    options: [],
    validation: { min_length: null, max_length: null, min_date: null, max_date: null },
    message_content: '',
    require_download: false,
    conditions: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Lazy-load IRB resources when the IRB tab is opened the first time
  useEffect(() => {
    if (activeRequestType === 'IRB' && irbDocs.length === 0 && irbInfo === '' && !irbLoading) {
      loadIRB();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRequestType]);

  const loadIRB = async () => {
    setIrbLoading(true);
    try {
      const res = await irbApi.get();
      setIrbInfo(res.data.info_text || '');
      setIrbDocs(res.data.documents || []);
    } catch {
      toast.error('Failed to load IRB resources');
    } finally {
      setIrbLoading(false);
    }
  };

  const handleSaveIRBInfo = async () => {
    setIrbInfoSaving(true);
    try {
      await irbApi.updateInfo(irbInfo);
      toast.success('IRB info saved');
    } catch {
      toast.error('Failed to save IRB info');
    } finally {
      setIrbInfoSaving(false);
    }
  };

  const handleUploadIRBDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIrbUploading(true);
    try {
      const res = await irbApi.uploadDoc(file);
      setIrbDocs((prev) => [...prev, res.data]);
      toast.success(`Uploaded ${file.name}`);
    } catch {
      toast.error('Failed to upload document');
    } finally {
      setIrbUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteIRBDoc = async (docId) => {
    if (!window.confirm('Delete this document? Company Reps will no longer see it.')) return;
    try {
      await irbApi.deleteDoc(docId);
      setIrbDocs((prev) => prev.filter((d) => d.id !== docId));
      toast.success('Document removed');
    } catch {
      toast.error('Failed to delete document');
    }
  };

  const handleSaveIRBDocDescription = async (docId, description) => {
    try {
      await irbApi.updateDocDescription(docId, description);
      setIrbDocs((prev) => prev.map((d) => (d.id === docId ? { ...d, description } : d)));
      toast.success('Description saved');
    } catch {
      toast.error('Failed to save description');
    }
  };

  const fetchData = async () => {
    try {
      const [policiesRes, templatesRes, categoriesRes, settingsRes, feaFormRes, ctaFormRes, ftFormRes, ftFormPathARes, ftFormPathBRes, feaCustomRes, ctaCustomRes, ftCustomRes, ftCustomPathARes, ftCustomPathBRes, feaNotifRes, ctaNotifRes, ftNotifRes, feaRoleDocRes, ctaRoleDocRes, ftRoleDocRes, feaDocNotifRes, ctaDocNotifRes, ftDocNotifRes, feaPslRes, ctaPslRes, ftPslRes, irbPslRes] = await Promise.all([
        adminApi.getReminderPolicies(),
        adminApi.getEmailTemplates(),
        adminApi.getEmailTemplateCategories(),
        requestTypeSettingsApi.getAll(),
        requestTypeSettingsApi.getFormConfig('FEA'),
        requestTypeSettingsApi.getFormConfig('CTA'),
        requestTypeSettingsApi.getFormConfig('FT').catch(() => ({ data: null })),
        requestTypeSettingsApi.getFormConfig('FT', 'A').catch(() => ({ data: null })),
        requestTypeSettingsApi.getFormConfig('FT', 'B').catch(() => ({ data: null })),
        requestTypeSettingsApi.getCustomFields('FEA'),
        requestTypeSettingsApi.getCustomFields('CTA'),
        requestTypeSettingsApi.getCustomFields('FT').catch(() => ({ data: { fields: [], field_order: [] } })),
        requestTypeSettingsApi.getCustomFields('FT', 'A').catch(() => ({ data: null })),
        requestTypeSettingsApi.getCustomFields('FT', 'B').catch(() => ({ data: null })),
        adminApi.getNotificationConfig('FEA').catch(() => ({ data: null })),
        adminApi.getNotificationConfig('CTA').catch(() => ({ data: null })),
        adminApi.getNotificationConfig('FT').catch(() => ({ data: null })),
        profileApi.getRoleDocConfigsByType('FEA').catch(() => ({ data: [] })),
        profileApi.getRoleDocConfigsByType('CTA').catch(() => ({ data: [] })),
        profileApi.getRoleDocConfigsByType('FT').catch(() => ({ data: [] })),
        profileApi.getDocNotifConfigs('FEA').catch(() => ({ data: {} })),
        profileApi.getDocNotifConfigs('CTA').catch(() => ({ data: {} })),
        profileApi.getDocNotifConfigs('FT').catch(() => ({ data: {} })),
        requestTypeSettingsApi.getPerStudyLimit('FEA').catch(() => ({ data: null })),
        requestTypeSettingsApi.getPerStudyLimit('CTA').catch(() => ({ data: null })),
        requestTypeSettingsApi.getPerStudyLimit('FT').catch(() => ({ data: null })),
        requestTypeSettingsApi.getPerStudyLimit('IRB').catch(() => ({ data: null })),
      ]);
      
      setReminderPolicies(policiesRes.data);
      setEmailTemplates(templatesRes.data);
      setEmailCategories(categoriesRes.data);
      setNotificationConfigs({ FEA: feaNotifRes.data, CTA: ctaNotifRes.data, FT: ftNotifRes.data });
      setRoleDocConfigs({ FEA: feaRoleDocRes.data || [], CTA: ctaRoleDocRes.data || [], FT: ftRoleDocRes.data || [] });
      setDocNotifConfigs({ FEA: feaDocNotifRes.data || {}, CTA: ctaDocNotifRes.data || {}, FT: ftDocNotifRes.data || {} });
      
      // Discussion settings per type
      const settingsMap = {};
      settingsRes.data.forEach(s => {
        settingsMap[s.request_type] = s.doc_thread_privileges;
      });
      setDiscussionSettings(settingsMap);
      
      // Form configs per type
      setFormConfigs({
        FEA: feaFormRes.data,
        CTA: ctaFormRes.data,
        FT: ftFormRes.data,
        FT_PATH_A: ftFormPathARes.data || ftFormRes.data,
        FT_PATH_B: ftFormPathBRes.data || ftFormRes.data,
      });
      
      // Custom fields per type (FT split by path)
      setCustomFields({
        FEA: feaCustomRes.data || { fields: [], field_order: [] },
        CTA: ctaCustomRes.data || { fields: [], field_order: [] },
        FT: ftCustomRes.data || { fields: [], field_order: [] },
        FT_PATH_A: ftCustomPathARes.data || { fields: [], field_order: [] },
        FT_PATH_B: ftCustomPathBRes.data || { fields: [], field_order: [] },
      });
      
      // Set unified field order (or generate default if not present)
      setUnifiedFieldOrder({
        FEA: feaFormRes.data?.unified_field_order || null,
        CTA: ctaFormRes.data?.unified_field_order || null,
        FT: ftFormRes.data?.unified_field_order || null,
        FT_PATH_A: ftFormPathARes.data?.unified_field_order || null,
        FT_PATH_B: ftFormPathBRes.data?.unified_field_order || null,
      });

      // iter167 Issue 35 — hydrate per-study limits from backend (fall back
      // to unlimited defaults when the backend returns no config yet).
      const _normPsl = (r) => {
        const d = r?.data || {};
        const mode = (d.mode === 'once' || d.mode === 'fixed') ? d.mode : 'unlimited';
        const max_count = Math.max(1, parseInt(d.max_count, 10) || 1);
        return { mode, max_count };
      };
      setPerStudyLimits({
        FEA: _normPsl(feaPslRes),
        CTA: _normPsl(ctaPslRes),
        FT:  _normPsl(ftPslRes),
        IRB: _normPsl(irbPslRes),
      });
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  // ========== REMINDER POLICIES ==========
  const handleEditPolicy = async (policy) => {
    setSelectedPolicy(policy);
    setPolicyForm({
      enabled: policy.enabled,
      send_email: policy.send_email,
      send_push: policy.send_push,
      initial_days: policy.initial_days,
      reminder_days: policy.reminder_days,
      max_reminders: policy.max_reminders,
      escalation_enabled: policy.escalation_enabled,
      escalation_after: policy.escalation_after
    });
    setShowPolicyModal(true);
  };

  const handleSavePolicy = async () => {
    if (!selectedPolicy) return;
    try {
      await adminApi.updateReminderPolicy(selectedPolicy.stage, policyForm);
      toast.success('Reminder policy updated');
      setShowPolicyModal(false);
      setSelectedPolicy(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update policy');
    }
  };

  const handleResetPolicy = async (stage) => {
    if (!window.confirm('Reset this policy to defaults?')) return;
    try {
      await adminApi.resetReminderPolicy(stage);
      toast.success('Policy reset to defaults');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset policy');
    }
  };

  const handleTriggerStageReminder = async (stage) => {
    try {
      const res = await adminApi.triggerStageReminder(stage);
      toast.success(`Reminder check triggered: ${res.data.result?.reminders_sent || 0} sent`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger reminder');
    }
  };

  // ========== EMAIL TEMPLATES ==========
  const handleEditTemplate = async (templateKey) => {
    try {
      const res = await adminApi.getEmailTemplate(templateKey);
      setSelectedTemplate(res.data);
      setTemplateForm({
        subject: res.data.subject,
        body: res.data.body,
        email_enabled: res.data.email_enabled !== false,
        push_enabled: res.data.push_enabled !== false
      });
      setShowTemplateModal(true);
    } catch (error) {
      toast.error('Failed to load template');
    }
  };

  const handleSaveTemplate = async () => {
    try {
      await adminApi.updateEmailTemplate(selectedTemplate.key, templateForm);
      toast.success('Template updated successfully');
      setShowTemplateModal(false);
      setSelectedTemplate(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update template');
    }
  };

  const handleToggleTemplateSettings = async (templateKey, field, value) => {
    try {
      const template = emailTemplates.find(t => t.key === templateKey);
      await adminApi.updateEmailTemplateSettings(templateKey, {
        email_enabled: field === 'email_enabled' ? value : template.email_enabled,
        push_enabled: field === 'push_enabled' ? value : template.push_enabled
      });
      toast.success('Settings updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handlePreviewTemplate = async (templateKey) => {
    try {
      const res = await adminApi.previewEmailTemplate(templateKey);
      setPreviewHtml(res.data.html_body);
      setShowPreviewModal(true);
    } catch (error) {
      toast.error('Failed to preview template');
    }
  };

  const handleResetTemplate = async (templateKey) => {
    if (!window.confirm('Reset this template to default? Your changes will be lost.')) return;
    try {
      await adminApi.resetEmailTemplate(templateKey);
      toast.success('Template reset to default');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset template');
    }
  };

  const handleTestEmail = async () => {
    try {
      await adminApi.testEmail({ to: testEmailAddress });
      toast.success(`Test email sent to ${testEmailAddress}`);
      setShowTestEmailModal(false);
      setTestEmailAddress('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    }
  };

  // ========== DISCUSSION SETTINGS ==========
  const handleTogglePrivilege = (key) => {
    if (PRIVILEGE_LABELS[key].locked) return;
    
    setDiscussionSettings(prev => ({
      ...prev,
      [activeRequestType]: {
        ...prev[activeRequestType],
        [key]: !prev[activeRequestType]?.[key]
      }
    }));
  };

  const handleSaveDiscussionSettings = async () => {
    setSavingSettings(true);
    try {
      await requestTypeSettingsApi.update(activeRequestType, discussionSettings[activeRequestType]);
      toast.success(`${activeRequestType} discussion settings saved`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // ========== PER-STUDY INSTANCE LIMIT (iter167 Issue 35) ==========
  // Controls how many requests of a given type can exist under one study:
  //   'unlimited' — no cap
  //   'once'      — exactly one per study
  //   'fixed'     — up to max_count per study
  const handlePerStudyLimitChange = (patch) => {
    setPerStudyLimits(prev => ({
      ...prev,
      [activeRequestType]: { ...prev[activeRequestType], ...patch },
    }));
  };

  const handleSavePerStudyLimit = async () => {
    const cfg = perStudyLimits[activeRequestType] || { mode: 'unlimited', max_count: 1 };
    const payload = {
      mode: cfg.mode || 'unlimited',
      max_count: Math.max(1, parseInt(cfg.max_count, 10) || 1),
    };
    setSavingPerStudyLimit(true);
    try {
      await requestTypeSettingsApi.updatePerStudyLimit(activeRequestType, payload);
      toast.success(`${activeRequestType} per-study limit saved`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save per-study limit');
    } finally {
      setSavingPerStudyLimit(false);
    }
  };

  // ========== FORM CONFIG HANDLERS ==========
  const handleFormConfigChange = (fieldName, property, value) => {
    const cfgKey = activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType;
    setFormConfigs(prev => ({
      ...prev,
      [cfgKey]: {
        ...prev[cfgKey],
        [fieldName]: {
          ...prev[cfgKey]?.[fieldName],
          [property]: value
        }
      }
    }));
  };

  // For FT, the effective config key incorporates the selected path (Path A or Path B)
  const ftConfigKey = () => activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType;
  
  const handleSaveFormConfig = async () => {
    setSavingFormConfig(true);
    try {
      const cfgKey = ftConfigKey();
      const orderKey = activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType;
      const configToSave = {
        ...formConfigs[cfgKey],
        unified_field_order: unifiedFieldOrder[orderKey] || null
      };
      // For FT, save under path-specific key
      const pathQuery = activeRequestType === 'FT' ? ftFormPath : undefined;
      await requestTypeSettingsApi.updateFormConfig(activeRequestType, configToSave, pathQuery);
      toast.success(`${activeRequestType}${pathQuery ? ` (Path ${pathQuery})` : ''} form configuration saved`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save form configuration');
    } finally {
      setSavingFormConfig(false);
    }
  };

  // ========== CUSTOM FIELD HANDLERS ==========
  const CUSTOM_FIELD_TYPES = [
    { value: 'text', label: 'Text Input', description: 'Single line text' },
    { value: 'textarea', label: 'Text Area', description: 'Multi-line text' },
    { value: 'select', label: 'Dropdown', description: 'Single choice selection' },
    { value: 'multiselect', label: 'Multi-Select', description: 'Multiple choice selection' },
    { value: 'date', label: 'Date Picker', description: 'Date selection' },
    { value: 'checkbox', label: 'Checkbox', description: 'Yes/No toggle' },
    { value: 'file_upload', label: 'File Upload', description: 'File attachment' },
    { value: 'file_download', label: 'File Download', description: 'Template file for download' },
    { value: 'message', label: 'Info Message', description: 'Display text/instructions' }
  ];

  const resetCustomFieldForm = () => {
    setCustomFieldForm({
      id: '',
      type: 'text',
      label: '',
      placeholder: '',
      help_text: '',
      required: false,
      enabled: true,
      options: [],
      validation: { min_length: null, max_length: null, min_date: null, max_date: null },
      message_content: '',
      require_download: false,
      conditions: []
    });
    setEditingCustomField(null);
  };

  const handleOpenAddCustomField = () => {
    resetCustomFieldForm();
    setShowCustomFieldModal(true);
  };

  const handleOpenEditCustomField = (field) => {
    setEditingCustomField(field);
    setCustomFieldForm({
      id: field.id,
      type: field.type,
      label: field.label || '',
      placeholder: field.placeholder || '',
      help_text: field.help_text || '',
      required: field.required || false,
      enabled: field.enabled !== false,
      options: field.options || [],
      validation: field.validation || { min_length: null, max_length: null, min_date: null, max_date: null },
      message_content: field.message_content || '',
      require_download: field.require_download || false,
      conditions: field.conditions || []
    });
    setShowCustomFieldModal(true);
  };

  const handleSaveCustomField = async () => {
    if (!customFieldForm.label.trim()) {
      toast.error('Field label is required');
      return;
    }
    
    // Generate ID from label if new field
    const fieldId = editingCustomField 
      ? customFieldForm.id 
      : customFieldForm.label.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_');
    
    const cfKey = activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType;
    const pathArg = activeRequestType === 'FT' ? ftFormPath : undefined;
    const fieldData = {
      ...customFieldForm,
      id: fieldId,
      order: editingCustomField ? editingCustomField.order : (customFields[cfKey]?.fields?.length || 0)
    };
    
    setSavingCustomField(true);
    try {
      if (editingCustomField) {
        await requestTypeSettingsApi.updateCustomField(activeRequestType, fieldId, fieldData, pathArg);
        toast.success('Custom field updated');
        setShowCustomFieldModal(false);
        resetCustomFieldForm();
      } else {
        await requestTypeSettingsApi.addCustomField(activeRequestType, fieldData, pathArg);
        toast.success('Custom field added');
        
        // For file_download type, keep modal open and switch to edit mode for template upload
        if (customFieldForm.type === 'file_download') {
          const res = await requestTypeSettingsApi.getCustomFields(activeRequestType, pathArg);
          setCustomFields(prev => ({
            ...prev,
            [cfKey]: res.data || { fields: [], field_order: [] }
          }));
          
          const newField = res.data?.fields?.find(f => f.id === fieldId);
          if (newField) {
            setEditingCustomField(newField);
            setCustomFieldForm({
              ...customFieldForm,
              id: fieldId
            });
            toast.info('Now you can upload the template file');
          }
        } else {
          setShowCustomFieldModal(false);
          resetCustomFieldForm();
        }
      }
      
      // Refresh custom fields
      const res = await requestTypeSettingsApi.getCustomFields(activeRequestType, pathArg);
      setCustomFields(prev => ({
        ...prev,
        [cfKey]: res.data || { fields: [], field_order: [] }
      }));
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save custom field');
    } finally {
      setSavingCustomField(false);
    }
  };

  const handleDeleteCustomField = async (fieldId) => {
    if (!confirm('Are you sure you want to delete this custom field?')) return;
    
    const cfKey = activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType;
    const pathArg = activeRequestType === 'FT' ? ftFormPath : undefined;
    try {
      await requestTypeSettingsApi.deleteCustomField(activeRequestType, fieldId, pathArg);
      toast.success('Custom field deleted');
      
      const res = await requestTypeSettingsApi.getCustomFields(activeRequestType, pathArg);
      setCustomFields(prev => ({
        ...prev,
        [cfKey]: res.data || { fields: [], field_order: [] }
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete custom field');
    }
  };

  const handleAddOption = () => {
    setCustomFieldForm(prev => ({
      ...prev,
      options: [...prev.options, { value: '', label: '' }]
    }));
  };

  const handleUpdateOption = (index, field, value) => {
    setCustomFieldForm(prev => {
      const newOptions = [...prev.options];
      newOptions[index] = { ...newOptions[index], [field]: value };
      // Auto-generate value from label if value is empty
      if (field === 'label' && !newOptions[index].value) {
        newOptions[index].value = value.toLowerCase().replace(/[^a-z0-9]/g, '_');
      }
      return { ...prev, options: newOptions };
    });
  };

  const handleRemoveOption = (index) => {
    setCustomFieldForm(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  // Handle drag end for reordering custom fields
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const cfKey = activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType;
      const pathArg = activeRequestType === 'FT' ? ftFormPath : undefined;
      const fields = customFields[cfKey]?.fields || [];
      const oldIndex = fields.findIndex(f => f.id === active.id);
      const newIndex = fields.findIndex(f => f.id === over.id);
      
      const newFields = arrayMove(fields, oldIndex, newIndex);
      const newOrder = newFields.map(f => f.id);
      
      setCustomFields(prev => ({
        ...prev,
        [cfKey]: { ...prev[cfKey], fields: newFields }
      }));
      
      try {
        await requestTypeSettingsApi.updateFieldOrder(activeRequestType, newOrder, pathArg);
        toast.success('Field order updated');
      } catch (error) {
        setCustomFields(prev => ({
          ...prev,
          [cfKey]: { ...prev[cfKey], fields }
        }));
        toast.error('Failed to update field order');
      }
    }
  };

  // ========== RENDER SECTIONS ==========
  const renderReminderPolicies = () => {
    const filteredPolicies = reminderPolicies.filter(p => p.request_type === activeRequestType);
    
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <p className="text-sm text-slate-600">
            Configure workflow stage reminders for {activeRequestType} requests. Reminders are sent via email and push notifications.
          </p>
        </div>
        
        <div className="space-y-3">
          {filteredPolicies.map(policy => (
            <div 
              key={policy.stage}
              className={`border rounded-lg p-4 ${policy.enabled ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200'}`}
              data-testid={`reminder-policy-${policy.stage}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-slate-900">{policy.label}</h4>
                    {!policy.enabled && (
                      <Badge variant="outline" className="text-slate-500">Disabled</Badge>
                    )}
                    {policy.is_default && (
                      <Badge variant="outline" className="text-blue-600 bg-blue-50">Default</Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{policy.description}</p>
                  
                  <div className="flex flex-wrap gap-4 mt-3 text-sm">
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span>First: {policy.initial_days}d</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <BellRing className="w-4 h-4 text-slate-400" />
                      <span>Every: {policy.reminder_days}d</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Bell className="w-4 h-4 text-slate-400" />
                      <span>Max: {policy.max_reminders}</span>
                    </div>
                    {policy.escalation_enabled && (
                      <div className="flex items-center gap-1.5">
                        <ArrowUpRight className="w-4 h-4 text-amber-500" />
                        <span className="text-amber-600">Escalate to {policy.escalation_role} after {policy.escalation_after}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-4 mt-2">
                    {policy.send_email && (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                        <Mail className="w-3 h-3 mr-1" /> Email
                      </Badge>
                    )}
                    {policy.send_push && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        <Bell className="w-3 h-3 mr-1" /> Push
                      </Badge>
                    )}
                    <span className="text-xs text-slate-400">Target: {policy.target_role}</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 ml-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleTriggerStageReminder(policy.stage)}
                    title="Test trigger this reminder"
                  >
                    <Play className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleEditPolicy(policy)}
                  >
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  {!policy.is_default && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleResetPolicy(policy.stage)}
                      title="Reset to defaults"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderEmailTemplates = () => {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <p className="text-sm text-slate-600">
            Configure email notifications. Enable/disable individual templates and customize content.
          </p>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowTestEmailModal(true)}
          >
            <Send size={16} className="mr-2" />
            Send Test Email
          </Button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Categories sidebar */}
          <div className="lg:col-span-1 space-y-2">
            {emailCategories.map(category => {
              const categoryTemplates = emailTemplates.filter(t => t.category === category.key);
              const enabledCount = categoryTemplates.filter(t => t.email_enabled !== false).length;
              
              return (
                <button
                  key={category.key}
                  onClick={() => setActiveEmailCategory(activeEmailCategory === category.key ? null : category.key)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    activeEmailCategory === category.key 
                      ? 'bg-blue-50 border-blue-200' 
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{category.label}</span>
                    <ChevronRight className={`w-4 h-4 transition-transform ${activeEmailCategory === category.key ? 'rotate-90' : ''}`} />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{enabledCount}/{categoryTemplates.length} enabled</p>
                </button>
              );
            })}
          </div>
          
          {/* Templates list */}
          <div className="lg:col-span-3">
            {activeEmailCategory ? (
              <div className="space-y-2">
                <h4 className="font-medium text-slate-900">
                  {emailCategories.find(c => c.key === activeEmailCategory)?.label}
                </h4>
                <p className="text-sm text-slate-500 mb-4">
                  {emailCategories.find(c => c.key === activeEmailCategory)?.description}
                </p>
                
                {emailTemplates
                  .filter(t => t.category === activeEmailCategory)
                  .map(template => (
                    <div 
                      key={template.key}
                      className="flex items-center justify-between p-3 border rounded-lg bg-white"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{formatTemplateKey(template.key)}</span>
                          {template.is_custom && (
                            <Badge className="bg-amber-100 text-amber-700">Customized</Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 truncate max-w-md">{template.subject}</p>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleToggleTemplateSettings(template.key, 'email_enabled', !template.email_enabled)}
                            className={`p-1.5 rounded ${template.email_enabled !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}
                            title="Email notifications"
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleTemplateSettings(template.key, 'push_enabled', !template.push_enabled)}
                            className={`p-1.5 rounded ${template.push_enabled !== false ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'}`}
                            title="Push notifications"
                          >
                            <Bell className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handlePreviewTemplate(template.key)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEditTemplate(template.key)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          {template.is_custom && (
                            <Button variant="ghost" size="sm" onClick={() => handleResetTemplate(template.key)}>
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-slate-500 bg-slate-50 rounded-lg">
                <div className="text-center">
                  <Mail className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Select a category to view templates</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // --- Upload dialog doc types (per request type) ---
  useEffect(() => {
    if (!['FEA', 'CTA', 'FT'].includes(activeRequestType)) return;
    requestTypeSettingsApi.getUploadDocTypes(activeRequestType)
      .then(res => {
        setUploadDocTypes(res.data.doc_types || []);
        setUploadDocTypeDefaults(res.data.defaults || []);
      })
      .catch(() => {});
    setNewUploadDocType('');
  }, [activeRequestType]);

  const handleAddUploadDocType = () => {
    const v = newUploadDocType.trim();
    if (!v) return;
    if (uploadDocTypes.some(t => t.toLowerCase() === v.toLowerCase())) {
      toast.error('This document type already exists');
      return;
    }
    setUploadDocTypes(prev => [...prev, v]);
    setNewUploadDocType('');
  };

  const handleSaveUploadDocTypes = async () => {
    if (uploadDocTypes.length === 0) {
      toast.error('At least one document type is required');
      return;
    }
    setUploadDocTypesSaving(true);
    try {
      await requestTypeSettingsApi.updateUploadDocTypes(activeRequestType, uploadDocTypes);
      toast.success(`Upload document types saved for ${activeRequestType}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save document types');
    } finally {
      setUploadDocTypesSaving(false);
    }
  };

  const renderUploadDocTypes = () => (
    <div className="space-y-4" data-testid="upload-doc-types-section">
      <p className="text-sm text-slate-500">
        These are the document type options users can pick when uploading a document from the
        Documents tab of a <strong>{activeRequestType}</strong> request. "Other (custom)" is always
        offered in addition to this list.
      </p>
      <div className="flex flex-wrap gap-2">
        {uploadDocTypes.map((type, idx) => (
          <Badge
            key={`${type}-${idx}`}
            variant="outline"
            className="pl-3 pr-1.5 py-1.5 text-sm font-normal flex items-center gap-1.5 bg-white"
            data-testid={`upload-doc-type-chip-${idx}`}
          >
            {type}
            <button
              type="button"
              className="text-slate-400 hover:text-red-600 transition-colors"
              onClick={() => setUploadDocTypes(prev => prev.filter((_, i) => i !== idx))}
              title="Remove"
              data-testid={`upload-doc-type-remove-${idx}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </Badge>
        ))}
        {uploadDocTypes.length === 0 && (
          <span className="text-sm text-slate-400 italic">No document types — add at least one.</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={newUploadDocType}
          onChange={(e) => setNewUploadDocType(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddUploadDocType(); } }}
          placeholder="Add a document type (e.g., Invoice)"
          className="max-w-xs"
          data-testid="upload-doc-type-input"
        />
        <Button type="button" variant="outline" size="sm" onClick={handleAddUploadDocType} data-testid="upload-doc-type-add-btn">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>
      <div className="flex items-center gap-2 pt-2 border-t">
        <Button
          onClick={handleSaveUploadDocTypes}
          disabled={uploadDocTypesSaving || uploadDocTypes.length === 0}
          className="bg-blue-900 hover:bg-blue-950"
          size="sm"
          data-testid="upload-doc-types-save-btn"
        >
          {uploadDocTypesSaving ? 'Saving…' : 'Save Document Types'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setUploadDocTypes(uploadDocTypeDefaults)}
          data-testid="upload-doc-types-reset-btn"
        >
          Reset to defaults
        </Button>
      </div>
    </div>
  );

  const renderDiscussionSettings = () => {    const privileges = discussionSettings[activeRequestType] || {};
    const docNotifs = getDocDiscussionNotifs();
    
    return (
      <div className="space-y-6">
        {/* Document & Discussion Notifications */}
        {docNotifs.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1 h-5 bg-blue-600 rounded"></div>
              <h3 className="font-semibold text-sm text-slate-900">Notification Events</h3>
            </div>
            <div className="space-y-1.5">
              {docNotifs.map(notification => (
                <div key={notification.key} className={`flex items-center justify-between p-3 border rounded-lg ${notification.channel === 'disabled' ? 'bg-slate-50' : 'bg-white'}`}>
                  <div className="flex-1 min-w-0">
                    <span className={`font-medium text-sm ${notification.channel === 'disabled' ? 'text-slate-400' : 'text-slate-900'}`}>{notification.label}</span>
                    <p className="text-xs text-slate-500 mt-0.5">{notification.description}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Select value={notification.channel} onValueChange={(value) => handleNotificationChange(notification.key, 'channel', value)}>
                      <SelectTrigger className="w-[130px] h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CHANNEL_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <span className={`px-1.5 py-0.5 rounded text-xs ${opt.color}`}>{opt.label}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Thread Visibility Privileges */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1 h-5 bg-violet-600 rounded"></div>
            <h3 className="font-semibold text-sm text-slate-900">Thread Visibility</h3>
          </div>
          <p className="text-sm text-slate-600 mb-3">
            Configure which roles can expand document thread visibility for {activeRequestType} requests
          </p>
          <div className="space-y-2">
            {Object.entries(PRIVILEGE_LABELS).map(([key, config]) => (
              <div key={key} className={`flex items-center justify-between p-3 rounded-lg border ${config.locked ? 'bg-slate-50' : 'bg-white'}`}>
                <div className="flex items-center gap-3">
                  <Switch checked={privileges[key] ?? config.locked} onCheckedChange={() => handleTogglePrivilege(key)} disabled={config.locked} />
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="font-medium text-sm">{config.label}</Label>
                      {config.locked && <Badge variant="outline" className="text-[10px] bg-slate-100"><Lock className="w-2.5 h-2.5 mr-0.5" />Locked</Badge>}
                    </div>
                    <p className="text-xs text-slate-500">{config.description}</p>
                  </div>
                </div>
                <Badge variant={privileges[key] ? "default" : "outline"} className={privileges[key] ? "bg-emerald-100 text-emerald-700" : "text-slate-500"}>
                  {privileges[key] ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
            ))}
          </div>
        </div>
        
        <div className="pt-4 border-t">
          <Button onClick={handleSaveDiscussionSettings} disabled={savingSettings} className="bg-blue-900 hover:bg-blue-950">
            {savingSettings ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    );
  };

  // iter167 Issue 35 — Per-study instance limit UI. Renders inside the
  // active request type's accordion (FEA / CTA / FT / IRB).
  const renderPerStudyLimit = () => {
    const cfg = perStudyLimits[activeRequestType] || { mode: 'unlimited', max_count: 1 };
    const mode = cfg.mode || 'unlimited';
    return (
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Control how many <span className="font-semibold text-slate-900">{activeRequestType}</span> requests can be added to a single Study. Terminal states (Rejected / Withdrawn / Failed / Closed) do <span className="font-medium">not</span> count toward this cap.
        </p>

        <div className="space-y-2">
          <label
            className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${mode === 'unlimited' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}
            data-testid={`per-study-limit-mode-unlimited-${activeRequestType}`}
          >
            <input
              type="radio"
              name={`psl-mode-${activeRequestType}`}
              value="unlimited"
              checked={mode === 'unlimited'}
              onChange={() => handlePerStudyLimitChange({ mode: 'unlimited' })}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-sm text-slate-900">Unlimited</div>
              <p className="text-xs text-slate-500">Any number of {activeRequestType} requests can be created under the same Study.</p>
            </div>
          </label>

          <label
            className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${mode === 'once' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}
            data-testid={`per-study-limit-mode-once-${activeRequestType}`}
          >
            <input
              type="radio"
              name={`psl-mode-${activeRequestType}`}
              value="once"
              checked={mode === 'once'}
              onChange={() => handlePerStudyLimitChange({ mode: 'once', max_count: 1 })}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-sm text-slate-900">Once per Study</div>
              <p className="text-xs text-slate-500">Only one active {activeRequestType} request is allowed per Study.</p>
            </div>
          </label>

          <label
            className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${mode === 'fixed' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}
            data-testid={`per-study-limit-mode-fixed-${activeRequestType}`}
          >
            <input
              type="radio"
              name={`psl-mode-${activeRequestType}`}
              value="fixed"
              checked={mode === 'fixed'}
              onChange={() => handlePerStudyLimitChange({ mode: 'fixed', max_count: Math.max(2, cfg.max_count || 2) })}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="font-medium text-sm text-slate-900">Fixed maximum</div>
              <p className="text-xs text-slate-500">Allow up to a specific number of active {activeRequestType} requests per Study.</p>
              {mode === 'fixed' && (
                <div className="flex items-center gap-2 mt-3">
                  <Label className="text-xs text-slate-600" htmlFor={`psl-max-${activeRequestType}`}>Maximum per Study</Label>
                  <Input
                    id={`psl-max-${activeRequestType}`}
                    type="number"
                    min="1"
                    max="999"
                    value={cfg.max_count || 1}
                    onChange={(e) => handlePerStudyLimitChange({ max_count: parseInt(e.target.value, 10) || 1 })}
                    className="w-24 h-8 text-sm"
                    data-testid={`per-study-limit-max-count-${activeRequestType}`}
                  />
                </div>
              )}
            </div>
          </label>
        </div>

        <div className="pt-4 border-t">
          <Button
            onClick={handleSavePerStudyLimit}
            disabled={savingPerStudyLimit}
            className="bg-blue-900 hover:bg-blue-950"
            data-testid={`per-study-limit-save-btn-${activeRequestType}`}
          >
            {savingPerStudyLimit ? 'Saving…' : 'Save Per-Study Limit'}
          </Button>
        </div>
      </div>
    );
  };

  // ========== NOTIFICATION CONFIG RENDER ==========
  const CHANNEL_OPTIONS = [
    { value: 'both', label: 'Email + In-App', color: 'bg-emerald-100 text-emerald-700' },
    { value: 'email', label: 'Email Only', color: 'bg-blue-100 text-blue-700' },
    { value: 'in_app', label: 'In-App Only', color: 'bg-amber-100 text-amber-700' },
    { value: 'disabled', label: 'Disabled', color: 'bg-slate-100 text-slate-500' },
  ];

  const ROLE_LABELS = {
    'ADMIN': 'Admin',
    'CRD': 'CRD',
    'CHAIRMAN': 'Chairman',
    'COORDINATOR': 'Coordinator',
    'DIRECTOR': 'Director',
    'EXEC_DIRECTOR': 'Exec Director',
    'INVESTIGATOR': 'Investigator',
    'REVIEWER': 'Reviewer',
    'COMPANY_REP': 'Company Rep'
  };

  const handleNotificationChange = async (notificationKey, field, value) => {
    const config = notificationConfigs[activeRequestType];
    if (!config) return;

    // Update local state optimistically
    const updatedNotifications = config.notifications.map(n => {
      if (n.key === notificationKey) {
        return { ...n, [field]: value };
      }
      return n;
    });
    
    setNotificationConfigs(prev => ({
      ...prev,
      [activeRequestType]: { ...config, notifications: updatedNotifications }
    }));

    // Save to backend
    setSavingNotification(notificationKey);
    try {
      await adminApi.updateNotificationConfig(activeRequestType, notificationKey, { [field]: value });
      toast.success('Notification setting updated');
    } catch (error) {
      toast.error('Failed to update notification setting');
      // Revert on error
      setNotificationConfigs(prev => ({
        ...prev,
        [activeRequestType]: config
      }));
    } finally {
      setSavingNotification(null);
    }
  };

  const handleToggleNotificationRole = async (notificationKey, role, currentRoles) => {
    const newRoles = currentRoles.includes(role)
      ? currentRoles.filter(r => r !== role)
      : [...currentRoles, role];
    
    await handleNotificationChange(notificationKey, 'enabled_roles', newRoles);
  };

  const handleTestNotification = async (notification) => {
    setTestingNotification(notification.key);
    try {
      const result = await adminApi.testNotification(activeRequestType, notification.key, {
        channel: notification.channel
      });
      
      if (result.data.email_sent || result.data.in_app_created) {
        toast.success(`Test notification sent! Check your ${notification.channel === 'both' ? 'email and notifications' : notification.channel === 'email' ? 'email' : 'notifications'}`);
      }
    } catch (error) {
      toast.error('Failed to send test notification');
    } finally {
      setTestingNotification(null);
    }
  };

  const handleResetNotifications = async () => {
    if (!confirm(`Reset all ${activeRequestType} notification settings to defaults?`)) return;
    
    try {
      await adminApi.resetNotificationConfig(activeRequestType);
      const res = await adminApi.getNotificationConfig(activeRequestType);
      setNotificationConfigs(prev => ({ ...prev, [activeRequestType]: res.data }));
      toast.success('Notification settings reset to defaults');
    } catch (error) {
      toast.error('Failed to reset notifications');
    }
  };

  // Role Document Config
  const handleSaveRoleDocConfig = async (role, documentTypes) => {
    setSavingRoleDoc(true);
    try {
      await profileApi.updateRoleDocConfig(role, { document_types: documentTypes, request_type: activeRequestType });
      toast.success(`Document requirements updated for ${role} (${activeRequestType})`);
      // Refresh
      const res = await profileApi.getRoleDocConfigsByType(activeRequestType);
      setRoleDocConfigs(prev => ({ ...prev, [activeRequestType]: res.data || [] }));
    } catch (error) {
      toast.error('Failed to save document config');
    } finally {
      setSavingRoleDoc(false);
    }
  };

  const renderNotificationSettings = () => {
    const config = notificationConfigs[activeRequestType];
    
    if (!config) {
      return (
        <div className="flex items-center justify-center h-32 text-slate-500">
          <div className="text-center">
            <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">Loading notification settings...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">
            Configure notifications for {activeRequestType} requests. Control which notifications are sent, through which channels, and to which roles.
          </p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleResetNotifications}
            className="text-slate-600"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Reset to Defaults
          </Button>
        </div>
        
        <div className="space-y-3">
          {config.notifications?.map((notification) => (
            <div 
              key={notification.key} 
              className={`p-4 rounded-lg border ${
                notification.channel === 'disabled' ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-slate-900">{notification.label}</span>
                    {savingNotification === notification.key && (
                      <Badge className="bg-blue-100 text-blue-700 text-xs">Saving...</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mb-3">{notification.description}</p>
                  
                  {/* Role toggles */}
                  <div className="flex flex-wrap gap-1.5">
                    {notification.applicable_roles?.map((role) => {
                      const isEnabled = notification.enabled_roles?.includes(role);
                      return (
                        <button
                          key={role}
                          onClick={() => handleToggleNotificationRole(notification.key, role, notification.enabled_roles || [])}
                          className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                            isEnabled 
                              ? 'bg-blue-50 border-blue-200 text-blue-700' 
                              : 'bg-slate-50 border-slate-200 text-slate-400 hover:bg-slate-100'
                          }`}
                          disabled={notification.channel === 'disabled'}
                        >
                          {ROLE_LABELS[role] || role}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Channel selector */}
                  <Select
                    value={notification.channel}
                    onValueChange={(value) => handleNotificationChange(notification.key, 'channel', value)}
                  >
                    <SelectTrigger className="w-[140px] h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CHANNEL_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <span className={`px-2 py-0.5 rounded text-xs ${opt.color}`}>
                            {opt.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Test button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestNotification(notification)}
                    disabled={notification.channel === 'disabled' || testingNotification === notification.key}
                    className="h-9"
                  >
                    {testingNotification === notification.key ? (
                      <span className="animate-pulse">Sending...</span>
                    ) : (
                      <>
                        <Send className="w-3 h-3 mr-1" />
                        Test
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ========== UNIFIED NOTIFICATIONS & REMINDERS ==========
  const ALL_SYSTEM_ROLES = [
    { value: 'ADMIN', label: 'Admin' },
    { value: 'CRD', label: 'CRD' },
    { value: 'CHAIRMAN', label: 'Chairman' },
    { value: 'COORDINATOR', label: 'Coordinator' },
    { value: 'INVESTIGATOR', label: 'Investigator' },
    { value: 'REVIEWER', label: 'Reviewer' },
    { value: 'COMPANY_REP', label: 'Company Rep' },
    { value: 'DIRECTOR', label: 'Director' },
    { value: 'EXEC_DIRECTOR', label: 'Exec Director' },
  ];

  const NOTIF_TO_TEMPLATE_MAP = {
    'request_submitted': 'request_submitted',
    'coordinator_assigned': 'coordinator_assigned',
    'fea_assigned': 'fea_assigned',
    'pi_assigned': 'pi_assigned',
    'reviewer_assigned': 'reviewer_assigned',
    'review_submitted': 'review_submitted',
    'director_review_required': 'director_review_required',
    'exec_director_review_required': 'exec_director_review_required',
    'request_rejected': 'request_rejected',
    'signature_required': 'signature_required',
    'signature_completed': 'signature_completed',
    'request_executed': 'request_executed',
    'request_closed': 'request_closed',
    'changes_requested': 'changes_requested',
    'comment_mention': 'comment_mention',
    'comment_group_mention': 'comment_group_mention',
    'fea_response_submitted': 'fea_response_submitted',
    'request_resubmitted': 'request_resubmitted',
    'fea_consultants_assigned': 'fea_consultants_assigned',
    'cta_team_assigned': 'cta_team_assigned',
  };

  const NOTIF_TO_REMINDER_MAP = {
    'FEA_fea_assigned': 'FEA_CONSULTANT_RESPONSE',
    'CTA_request_submitted': 'CTA_CHAIRMAN_ASSIGNMENT',
    'CTA_coordinator_assigned': 'CTA_COORDINATOR_ACTION',
    'CTA_pi_assigned': 'CTA_PI_REVIEW',
    'CTA_reviewer_assigned': 'CTA_REVIEWER_REVIEW',
    'CTA_reviews_complete_pending_chairman': 'CTA_CHAIRMAN_FINAL',
    'CTA_director_review_required': 'CTA_DIRECTOR_REVIEW',
    'CTA_exec_director_review_required': 'CTA_EXEC_DIRECTOR_REVIEW',
    'CTA_signature_required': 'CTA_SIGNATURE_PENDING',
  };

  const [expandedNotifEvents, setExpandedNotifEvents] = useState({});
  const [showAddRoleDropdown, setShowAddRoleDropdown] = useState(null);

  const toggleNotifEvent = (key) => {
    setExpandedNotifEvents(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Add a custom reminder to a notification event
  const handleAddCustomReminder = async (notificationKey) => {
    const defaultReminder = {
      enabled: true,
      initial_days: 3,
      reminder_days: 2,
      max_reminders: 3,
      escalation_enabled: false,
      escalation_role: 'ADMIN',
    };
    await handleNotificationChange(notificationKey, 'custom_reminder', defaultReminder);
    toast.success('Reminder added');
  };

  const handleUpdateCustomReminder = async (notificationKey, field, value) => {
    const config = notificationConfigs[activeRequestType];
    const notif = config?.notifications?.find(n => n.key === notificationKey);
    const current = notif?.custom_reminder || {};
    const updated = { ...current, [field]: value };
    await handleNotificationChange(notificationKey, 'custom_reminder', updated);
  };

  const handleRemoveCustomReminder = async (notificationKey) => {
    await handleNotificationChange(notificationKey, 'custom_reminder', null);
    toast.success('Reminder removed');
  };

  // Add role to notification recipients
  const handleAddRole = async (notificationKey, role, currentRoles) => {
    if (!currentRoles.includes(role)) {
      await handleNotificationChange(notificationKey, 'enabled_roles', [...currentRoles, role]);
    }
    setShowAddRoleDropdown(null);
  };

  const renderUnifiedNotifications = () => {
    const config = notificationConfigs[activeRequestType];
    if (!config?.notifications) {
      return <div className="text-slate-500 text-sm">Loading notifications...</div>;
    }

    // Only workflow events (sorted by step_order)
    const workflowEvents = config.notifications
      .filter(n => n.phase === 'workflow')
      .sort((a, b) => (a.step_order || 99) - (b.step_order || 99));

    // Build template & reminder lookups
    const templateMap = {};
    emailTemplates.forEach(t => { templateMap[t.key] = t; });
    const reminderMap = {};
    reminderPolicies
      .filter(p => p.request_type === activeRequestType)
      .forEach(p => { reminderMap[p.stage] = p; });

    const enabledCount = workflowEvents.filter(n => n.channel !== 'disabled').length;

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">
            {activeRequestType} workflow notifications in sequence. Each step shows its notification, recipients, and follow-up reminder.
          </p>
          <Button variant="outline" size="sm" onClick={handleResetNotifications} className="text-slate-600">
            <RotateCcw className="w-4 h-4 mr-1" /> Reset All
          </Button>
        </div>

        {/* Workflow sequence connector */}
        <div className="relative space-y-0">
          {workflowEvents.map((notification, idx) => {
            const isExpanded = expandedNotifEvents[notification.key];
            const linkedTemplateKey = NOTIF_TO_TEMPLATE_MAP[notification.key];
            const linkedTemplate = linkedTemplateKey ? templateMap[linkedTemplateKey] : null;
            const reminderStageKey = NOTIF_TO_REMINDER_MAP[`${activeRequestType}_${notification.key}`];
            const linkedReminder = reminderStageKey ? reminderMap[reminderStageKey] : null;
            const customReminder = notification.custom_reminder;
            const hasReminder = linkedReminder || customReminder;
            const isLast = idx === workflowEvents.length - 1;

            return (
              <div key={notification.key} className="relative" data-testid={`notif-event-${notification.key}`}>
                {/* Connector line */}
                {!isLast && (
                  <div className="absolute left-5 top-[44px] bottom-0 w-px bg-slate-200 z-0" />
                )}

                <div className={`relative z-10 border rounded-lg overflow-hidden ml-10 mb-2 ${
                  notification.channel === 'disabled' ? 'bg-slate-50/80 border-slate-200' : 'bg-white border-slate-200'
                }`}>
                  {/* Step indicator */}
                  <div className="absolute -left-10 top-3 w-6 h-6 rounded-full bg-white border-2 border-blue-400 flex items-center justify-center text-[10px] font-bold text-blue-600 z-20">
                    {idx + 1}
                  </div>

                  {/* Event Row */}
                  <div className="flex items-center justify-between px-4 py-3 gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-medium text-sm ${notification.channel === 'disabled' ? 'text-slate-400' : 'text-slate-900'}`}>
                          {notification.label}
                        </span>
                        {hasReminder && (linkedReminder?.enabled !== false || customReminder?.enabled !== false) && (
                          <Badge className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0">
                            <Clock className="w-2.5 h-2.5 mr-0.5" />
                            {(linkedReminder || customReminder)?.initial_days || customReminder?.initial_days}d
                          </Badge>
                        )}
                        {savingNotification === notification.key && (
                          <Badge className="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0">Saving...</Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{notification.description}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Select value={notification.channel} onValueChange={(value) => handleNotificationChange(notification.key, 'channel', value)}>
                        <SelectTrigger className="w-[130px] h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CHANNEL_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              <span className={`px-1.5 py-0.5 rounded text-xs ${opt.color}`}>{opt.label}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button variant="outline" size="sm" onClick={() => handleTestNotification(notification)} disabled={notification.channel === 'disabled' || testingNotification === notification.key} className="h-8 px-2">
                        {testingNotification === notification.key ? <span className="animate-pulse text-xs">...</span> : <Send className="w-3 h-3" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => toggleNotifEvent(notification.key)} className="h-8 px-2">
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Detail */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-1 bg-slate-50/50 border-t space-y-3">
                      {/* Recipients with add role */}
                      <div>
                        <Label className="text-xs text-slate-500 mb-1.5 block">Recipients</Label>
                        <div className="flex flex-wrap gap-1.5 items-center">
                          {notification.enabled_roles?.map((role) => (
                            <div key={role} className="flex items-center gap-0.5 px-2.5 py-1 text-xs rounded-full bg-blue-50 border border-blue-200 text-blue-700">
                              <span>{ROLE_LABELS[role] || role}</span>
                              <button
                                onClick={() => {
                                  const newRoles = notification.enabled_roles.filter(r => r !== role);
                                  handleNotificationChange(notification.key, 'enabled_roles', newRoles);
                                }}
                                className="ml-1 hover:text-red-500"
                                disabled={notification.channel === 'disabled'}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                          {/* Add role dropdown */}
                          <div className="relative">
                            {showAddRoleDropdown === notification.key ? (
                              <Select
                                value=""
                                onValueChange={(role) => handleAddRole(notification.key, role, notification.enabled_roles || [])}
                                open={true}
                                onOpenChange={(open) => { if (!open) setShowAddRoleDropdown(null); }}
                              >
                                <SelectTrigger className="h-7 w-[120px] text-[10px]"><SelectValue placeholder="Add role..." /></SelectTrigger>
                                <SelectContent>
                                  {ALL_SYSTEM_ROLES
                                    .filter(r => !(notification.enabled_roles || []).includes(r.value))
                                    .map(r => (
                                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <button
                                onClick={() => setShowAddRoleDropdown(notification.key)}
                                className="px-2 py-1 text-xs rounded-full border border-dashed border-slate-300 text-slate-400 hover:border-blue-400 hover:text-blue-600 transition-colors"
                                disabled={notification.channel === 'disabled'}
                              >
                                <Plus className="w-3 h-3 inline mr-0.5" /> Add
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Inline Reminder (linked or custom) */}
                      {linkedReminder && (
                        <div className="p-3 bg-amber-50/50 rounded-lg border border-amber-200/50 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-amber-800 flex items-center gap-1.5">
                              <Clock className="w-3 h-3" /> Follow-up Reminder
                            </span>
                            <div className="flex items-center gap-1.5">
                              <Button variant="outline" size="sm" className="h-6 px-2 text-[10px] bg-white" onClick={() => handleTriggerStageReminder(linkedReminder.stage)}>
                                <Play className="w-2.5 h-2.5 mr-0.5" /> Test
                              </Button>
                              <Button variant="outline" size="sm" className="h-6 px-2 text-[10px] bg-white" onClick={() => handleEditPolicy(linkedReminder)}>
                                <Edit className="w-2.5 h-2.5 mr-0.5" /> Edit
                              </Button>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-amber-700">
                            <span>First after <strong>{linkedReminder.initial_days}d</strong></span>
                            <span>Then every <strong>{linkedReminder.reminder_days}d</strong></span>
                            <span>Max <strong>{linkedReminder.max_reminders}</strong></span>
                            {linkedReminder.escalation_enabled && (
                              <span className="flex items-center gap-1">
                                <ArrowUpRight className="w-3 h-3" /> Escalate to <strong>{notification.escalation_role || linkedReminder.escalation_role}</strong>
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Custom Reminder (added by admin to events without linked reminder) */}
                      {!linkedReminder && customReminder && (
                        <div className="p-3 bg-amber-50/50 rounded-lg border border-amber-200/50 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-amber-800 flex items-center gap-1.5">
                              <Clock className="w-3 h-3" /> Custom Reminder
                            </span>
                            <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-red-500 hover:text-red-700" onClick={() => handleRemoveCustomReminder(notification.key)}>
                              <Trash2 className="w-2.5 h-2.5 mr-0.5" /> Remove
                            </Button>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <Label className="text-[10px] text-amber-700">First after (days)</Label>
                              <Input type="number" className="h-7 text-xs mt-0.5" value={customReminder.initial_days || 3}
                                onChange={(e) => handleUpdateCustomReminder(notification.key, 'initial_days', parseInt(e.target.value) || 1)} />
                            </div>
                            <div>
                              <Label className="text-[10px] text-amber-700">Repeat every (days)</Label>
                              <Input type="number" className="h-7 text-xs mt-0.5" value={customReminder.reminder_days || 2}
                                onChange={(e) => handleUpdateCustomReminder(notification.key, 'reminder_days', parseInt(e.target.value) || 1)} />
                            </div>
                            <div>
                              <Label className="text-[10px] text-amber-700">Max reminders</Label>
                              <Input type="number" className="h-7 text-xs mt-0.5" value={customReminder.max_reminders || 3}
                                onChange={(e) => handleUpdateCustomReminder(notification.key, 'max_reminders', parseInt(e.target.value) || 1)} />
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Switch checked={customReminder.escalation_enabled || false}
                              onCheckedChange={(checked) => handleUpdateCustomReminder(notification.key, 'escalation_enabled', checked)} />
                            <Label className="text-xs text-amber-700">Escalate</Label>
                            {customReminder.escalation_enabled && (
                              <Select value={customReminder.escalation_role || 'ADMIN'}
                                onValueChange={(val) => handleUpdateCustomReminder(notification.key, 'escalation_role', val)}>
                                <SelectTrigger className="h-7 w-[120px] text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {ALL_SYSTEM_ROLES.map(r => (
                                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Add Reminder button (for events without any reminder) */}
                      {!linkedReminder && !customReminder && (
                        <button
                          onClick={() => handleAddCustomReminder(notification.key)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-dashed border-amber-300 text-amber-600 hover:bg-amber-50 transition-colors"
                        >
                          <Plus className="w-3 h-3" /> Add Follow-up Reminder
                        </button>
                      )}

                      {/* Linked Email Template */}
                      {linkedTemplate && (
                        <div className="flex items-center justify-between p-2.5 bg-white rounded-md border">
                          <div className="flex items-center gap-2 min-w-0">
                            <Mail className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                            <p className="text-xs font-medium text-slate-700 truncate">{linkedTemplate.subject}</p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => handlePreviewTemplate(linkedTemplate.key)}>
                              <Eye className="w-3 h-3 mr-1" /> Preview
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => handleEditTemplate(linkedTemplate.key)}>
                              <Edit className="w-3 h-3 mr-1" /> Edit Template
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Get document/discussion notification events for the Discussion Settings section
  const getDocDiscussionNotifs = () => {
    const config = notificationConfigs[activeRequestType];
    if (!config?.notifications) return [];
    return config.notifications.filter(n => n.phase === 'documents');
  };

    // ========== ROLE DOCUMENT REQUIREMENTS RENDER ==========
  const renderRoleDocRequirements = () => {
    const allConfigs = roleDocConfigs[activeRequestType] || [];
    // Per-type involved roles (only show roles that actually participate in the workflow)
    const INVOLVED_ROLES_BY_TYPE = {
      FEA: ['COMPANY_REP', 'CHAIRMAN', 'COORDINATOR', 'INVESTIGATOR', 'REVIEWER', 'CRD', 'ADMIN', 'PI', 'CONSULTANT'],
      CTA: ['COMPANY_REP', 'CHAIRMAN', 'COORDINATOR', 'INVESTIGATOR', 'REVIEWER', 'CRD', 'ADMIN', 'DIRECTOR', 'EXEC_DIRECTOR', 'PI', 'CONSULTANT'],
      FT: ['COORDINATOR', 'CHAIRMAN', 'FINANCE', 'DIRECTOR', 'EXEC_DIRECTOR', 'ADMIN'],
      IRB: ['COMPANY_REP', 'CHAIRMAN', 'COORDINATOR', 'INVESTIGATOR', 'REVIEWER', 'CRD', 'ADMIN'],
    };
    const involvedRoles = INVOLVED_ROLES_BY_TYPE[activeRequestType] || [];
    const currentConfigs = allConfigs.filter(rc => involvedRoles.includes(rc.role));
    // Get doc notification events from the notification config
    const config = notificationConfigs[activeRequestType];
    const docNotifEvents = config?.notifications?.filter(n => n.phase === 'system') || [];
    
    return (
      <div className="space-y-6">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 leading-relaxed">
          <strong>Strict per-type configuration.</strong> Each (role, request type) pair is independent — there is no fallback from one type to another. If a role has no documents listed under <span className="font-semibold">{activeRequestType}</span>, no documents will be required for that role on {activeRequestType} requests. Configure each type explicitly.
        </div>
        <p className="text-sm text-slate-600">
          Configure which documents are required for each role. Users will see these on their profile page.
        </p>
        
        <div className="space-y-3">
          {currentConfigs.map(roleConfig => {
            const docCount = roleConfig.document_types?.length || 0;
            const isExpanded = editingRoleDoc === roleConfig.role;
            
            return (
              <div key={roleConfig.role} className="border rounded-lg overflow-hidden" data-testid={`role-doc-${roleConfig.role}`}>
                <button
                  onClick={() => setEditingRoleDoc(isExpanded ? null : roleConfig.role)}
                  className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="font-mono text-xs">{roleConfig.role}</Badge>
                    <span className={`text-sm ${docCount === 0 ? 'text-amber-700' : 'text-slate-600'}`}>
                      {docCount > 0
                        ? `${docCount} document${docCount !== 1 ? 's' : ''} required`
                        : `No documents required for ${activeRequestType}`}
                    </span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>
                
                {isExpanded && (
                  <div className="px-4 pb-4 pt-2 border-t bg-slate-50/50 space-y-3">
                    {(roleConfig.document_types || []).map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2.5 bg-white rounded-md border" data-testid={`role-doc-row-${roleConfig.role}-${idx}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <span className="font-medium text-sm">{doc.name}</span>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              {doc.has_expiry && <Badge className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0">Expires</Badge>}
                              <Badge className={`text-[10px] px-1.5 py-0 ${doc.enforce === 'block_assign' ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'}`}>
                                {doc.enforce === 'block_assign' ? 'Blocks Assignment' : 'Warning Only'}
                              </Badge>
                              {doc.notify_days_before > 0 && (
                                <Badge className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0">Notify {doc.notify_days_before}d before</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-600 hover:text-blue-600"
                            data-testid={`role-doc-edit-${roleConfig.role}-${idx}`}
                            onClick={() => {
                              setDocModalRole(roleConfig.role);
                              setDocModalIdx(idx);
                              setDocModalForm({
                                name: doc.name || '',
                                has_expiry: !!doc.has_expiry,
                                enforce: doc.enforce || 'warning',
                                notify_days_before: doc.notify_days_before || 30,
                              });
                              setDocModalOpen(true);
                            }}
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500 hover:text-red-700"
                            data-testid={`role-doc-delete-${roleConfig.role}-${idx}`}
                            onClick={() => {
                              if (!window.confirm(`Remove required document "${doc.name}" for ${roleConfig.role}?`)) return;
                              const updated = roleConfig.document_types.filter((_, i) => i !== idx);
                              handleSaveRoleDocConfig(roleConfig.role, updated);
                            }}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}

                    <Button variant="outline" size="sm" className="w-full border-dashed"
                      data-testid={`role-doc-add-${roleConfig.role}`}
                      onClick={() => {
                        setDocModalRole(roleConfig.role);
                        setDocModalIdx(null);
                        setDocModalForm({ name: '', has_expiry: false, enforce: 'warning', notify_days_before: 30 });
                        setDocModalOpen(true);
                      }}
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add Required Document
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Add/Edit Required Document Modal */}
        <Dialog open={docModalOpen} onOpenChange={(open) => { if (!open) setDocModalOpen(false); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{docModalIdx === null ? 'Add Required Document' : 'Edit Required Document'}</DialogTitle>
              <DialogDescription>
                For role <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{docModalRole}</span>. Users in this role will see this document on their profile.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="role-doc-name" className="text-sm font-medium">Document name</Label>
                <Input
                  id="role-doc-name"
                  data-testid="role-doc-modal-name"
                  placeholder="e.g., Medical License, CV, Good Clinical Practice (GCP)"
                  value={docModalForm.name}
                  onChange={(e) => setDocModalForm(f => ({ ...f, name: e.target.value }))}
                  autoFocus
                />
              </div>

              <div className="flex items-center justify-between p-2.5 rounded-md border border-slate-200 bg-slate-50/60">
                <div className="min-w-0 pr-3">
                  <Label htmlFor="role-doc-has-expiry" className="text-sm font-medium cursor-pointer">Has expiry date</Label>
                  <p className="text-xs text-slate-500 mt-0.5">Toggle on if this document expires (e.g., licenses, certifications).</p>
                </div>
                <Switch
                  id="role-doc-has-expiry"
                  data-testid="role-doc-modal-has-expiry"
                  checked={docModalForm.has_expiry}
                  onCheckedChange={(v) => setDocModalForm(f => ({ ...f, has_expiry: !!v, notify_days_before: v ? (f.notify_days_before || 30) : 0 }))}
                />
              </div>

              {docModalForm.has_expiry && (
                <div className="space-y-1.5">
                  <Label htmlFor="role-doc-notify" className="text-sm font-medium">Notify days before expiry</Label>
                  <Input
                    id="role-doc-notify"
                    data-testid="role-doc-modal-notify-days"
                    type="number"
                    min={1}
                    max={365}
                    value={docModalForm.notify_days_before}
                    onChange={(e) => setDocModalForm(f => ({ ...f, notify_days_before: Math.max(1, parseInt(e.target.value || '30', 10)) }))}
                  />
                  <p className="text-xs text-slate-500">Send a reminder this many days before the document expires.</p>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-sm font-medium">Enforcement</Label>
                <div className="grid gap-2">
                  <label className={`flex items-start gap-2 p-2.5 rounded-md border cursor-pointer transition-colors ${docModalForm.enforce === 'warning' ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                    <input
                      type="radio"
                      name="role-doc-enforce"
                      data-testid="role-doc-modal-enforce-warning"
                      checked={docModalForm.enforce === 'warning'}
                      onChange={() => setDocModalForm(f => ({ ...f, enforce: 'warning' }))}
                      className="mt-1"
                    />
                    <div>
                      <div className="text-sm font-medium">Warning only</div>
                      <div className="text-xs text-slate-500">Show a warning if missing/expired but still allow assignment.</div>
                    </div>
                  </label>
                  <label className={`flex items-start gap-2 p-2.5 rounded-md border cursor-pointer transition-colors ${docModalForm.enforce === 'block_assign' ? 'border-red-400 bg-red-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                    <input
                      type="radio"
                      name="role-doc-enforce"
                      data-testid="role-doc-modal-enforce-block"
                      checked={docModalForm.enforce === 'block_assign'}
                      onChange={() => setDocModalForm(f => ({ ...f, enforce: 'block_assign' }))}
                      className="mt-1"
                    />
                    <div>
                      <div className="text-sm font-medium">Block assignment</div>
                      <div className="text-xs text-slate-500">Prevent assigning this user to a request when this document is missing.</div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDocModalOpen(false)} disabled={savingRoleDoc}>Cancel</Button>
              <Button
                data-testid="role-doc-modal-submit"
                disabled={!docModalForm.name.trim() || savingRoleDoc}
                onClick={async () => {
                  const cfg = (roleDocConfigs[activeRequestType] || []).find(r => r.role === docModalRole);
                  if (!cfg) return;
                  const item = {
                    name: docModalForm.name.trim(),
                    has_expiry: !!docModalForm.has_expiry,
                    enforce: docModalForm.enforce,
                    notify_days_before: docModalForm.has_expiry ? (docModalForm.notify_days_before || 30) : 0,
                  };
                  let updated;
                  if (docModalIdx === null) {
                    updated = [...(cfg.document_types || []), item];
                  } else {
                    updated = (cfg.document_types || []).map((d, i) => i === docModalIdx ? item : d);
                  }
                  await handleSaveRoleDocConfig(docModalRole, updated);
                  setDocModalOpen(false);
                }}
              >
                {docModalIdx === null ? 'Add Document' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Notification Events for Document Expiry */}
        <div className="pt-4 border-t">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1 h-5 bg-blue-600 rounded"></div>
            <h3 className="font-semibold text-sm text-slate-900">Notification Events</h3>
          </div>
          <div className="space-y-2">
            {DOC_NOTIFICATION_EVENTS.map(notif => {
              const isExpanded = expandedDocNotif[notif.key];
              const currentConfig = docNotifConfigs[activeRequestType]?.[notif.key] || { channel: notif.defaultChannel, recipients: notif.defaultRecipients };
              
              const handleChannelChange = async (newChannel) => {
                const updated = { ...docNotifConfigs[activeRequestType], [notif.key]: { ...currentConfig, channel: newChannel } };
                setDocNotifConfigs(prev => ({ ...prev, [activeRequestType]: updated }));
                try {
                  await profileApi.updateDocNotifConfigs(activeRequestType, { notifications: updated });
                  toast.success(`${notif.label} channel updated`);
                } catch (e) { toast.error('Failed to save'); }
              };
              
              const handleToggleRecipient = async (role) => {
                const currentRecipients = currentConfig.recipients || [];
                const newRecipients = currentRecipients.includes(role) 
                  ? currentRecipients.filter(r => r !== role)
                  : [...currentRecipients, role];
                const updated = { ...docNotifConfigs[activeRequestType], [notif.key]: { ...currentConfig, recipients: newRecipients } };
                setDocNotifConfigs(prev => ({ ...prev, [activeRequestType]: updated }));
                try {
                  await profileApi.updateDocNotifConfigs(activeRequestType, { notifications: updated });
                } catch (e) { toast.error('Failed to save'); }
              };
              
              const handleAddRecipient = async (role) => {
                if (!currentConfig.recipients?.includes(role)) {
                  await handleToggleRecipient(role);
                }
              };
              
              return (
                <div key={notif.key} className="border rounded-lg bg-white overflow-hidden" data-testid={`doc-notif-${notif.key}`}>
                  <div className="flex items-center justify-between px-4 py-3 gap-4">
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-sm text-slate-900">{notif.label}</span>
                      <p className="text-xs text-slate-500 mt-0.5">{notif.desc}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Select value={currentConfig.channel || notif.defaultChannel} onValueChange={handleChannelChange}>
                        <SelectTrigger className="w-[130px] h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CHANNEL_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              <span className={`px-1.5 py-0.5 rounded text-xs ${opt.color}`}>{opt.label}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" className="h-8 px-2"
                        onClick={() => setExpandedDocNotif(prev => ({ ...prev, [notif.key]: !prev[notif.key] }))}
                      >
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                      </Button>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-1 bg-slate-50/50 border-t space-y-3">
                      <div>
                        <Label className="text-xs text-slate-500 mb-1.5 block">Recipients</Label>
                        <div className="flex flex-wrap gap-1.5">
                          {(currentConfig.recipients || []).map(role => (
                            <div key={role} className="flex items-center gap-0.5 px-2.5 py-1 text-xs rounded-full bg-blue-50 border border-blue-200 text-blue-700">
                              {ROLE_LABELS[role] || role}
                              <button onClick={() => handleToggleRecipient(role)} className="ml-1 hover:text-red-500">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                          <div className="relative">
                            <Select value="" onValueChange={(role) => handleAddRecipient(role)}>
                              <SelectTrigger className="h-7 w-[100px] text-[10px] border-dashed"><SelectValue placeholder="+ Add" /></SelectTrigger>
                              <SelectContent>
                                {['USER', 'ADMIN', 'CRD', 'CHAIRMAN', 'COORDINATOR', 'INVESTIGATOR', 'REVIEWER', 'DIRECTOR', 'EXEC_DIRECTOR', 'COMPANY_REP']
                                  .filter(r => !(currentConfig.recipients || []).includes(r))
                                  .map(r => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-2.5 bg-white rounded-md border">
                        <div className="flex items-center gap-2 min-w-0">
                          <Mail className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                          <p className="text-xs font-medium text-slate-700 truncate">{notif.templateSubject}</p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => handlePreviewTemplate(notif.key)}>
                            <Eye className="w-3 h-3 mr-1" /> Preview
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => handleEditTemplate(notif.key)}>
                            <Edit className="w-3 h-3 mr-1" /> Edit Template
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

    // ========== FORM CONFIG RENDER ==========
  const FORM_FIELD_LABELS = {
    title: { defaultLabel: 'Study Title', description: 'Main title for the request', alwaysEnabled: true, icon: 'FileText' },
    description: { defaultLabel: 'Description', description: 'Detailed description of the study', alwaysEnabled: true, icon: 'AlignLeft' },
    specialties: { defaultLabel: 'Specialties', description: 'Medical specialties for the request', alwaysEnabled: true, icon: 'Stethoscope' },
    disease_condition: { defaultLabel: 'Disease/Condition', description: 'Target disease or condition (FEA specific)', icon: 'Heart' },
    protocol_summary: { defaultLabel: 'Protocol Summary', description: 'Brief summary of the study protocol', icon: 'FileCheck' },
    linked_fea: { defaultLabel: 'Link to Previous FEA', description: 'Link CTA to a previous Feasibility Study (CTA specific)', icon: 'Link' }
  };
  
  // FT-specific field set per Path (A = link to existing study, B = create new study)
  // These mirror the actual sections shown to the coordinator on the New FT form.
  const FT_PATH_A_FIELD_LABELS = {
    title: { defaultLabel: 'FT Title', description: 'Main title for the Financial Transaction', alwaysEnabled: true, icon: 'FileText' },
    description: { defaultLabel: 'Description', description: 'Detailed description of the financial transaction', icon: 'AlignLeft' },
    protocol_summary: { defaultLabel: 'Protocol Summary', description: 'Brief summary or notes', icon: 'FileCheck' },
    cta_display: { defaultLabel: 'Linked CTA', description: 'Auto-linked CTA chip from the selected study (read-only)', icon: 'Link' },
    investigators: { defaultLabel: 'Investigators (PI + Co-Investigators)', description: 'PI and Co-I — inherited from CTA/FEA when available, otherwise picker', icon: 'Users' },
  };
  const FT_PATH_B_FIELD_LABELS = {
    title: { defaultLabel: 'FT Title', description: 'Main title for the Financial Transaction', alwaysEnabled: true, icon: 'FileText' },
    description: { defaultLabel: 'Description', description: 'Detailed description of the financial transaction', icon: 'AlignLeft' },
    protocol_summary: { defaultLabel: 'Protocol Summary', description: 'Brief summary or notes', icon: 'FileCheck' },
    manual_cta_ref_no: { defaultLabel: 'CTA Number', description: 'Manually-entered CTA reference number (legacy CTA not yet on platform)', icon: 'FileText' },
    manual_cta_title: { defaultLabel: 'CTA Name', description: 'Manually-entered CTA title', icon: 'FileText' },
    investigators: { defaultLabel: 'Investigators (PI + Co-Investigators)', description: 'PI and Co-I picker', icon: 'Users' },
  };
  
  // Helper: return the active field-label map for the currently selected request type / FT path
  const getActiveFieldLabels = () => {
    if (activeRequestType === 'FT') {
      return ftFormPath === 'B' ? FT_PATH_B_FIELD_LABELS : FT_PATH_A_FIELD_LABELS;
    }
    return FORM_FIELD_LABELS;
  };

  // Field type icons for custom fields
  const FIELD_TYPE_ICONS = {
    text: '📝',
    textarea: '📋',
    number: '🔢',
    date: '📅',
    select: '📑',
    checkbox: '☑️',
    url: '🔗',
    message: '💬',
    email: '✉️',
    phone: '📞'
  };

  // Expanded fields state for collapsible cards
  const [expandedFields, setExpandedFields] = useState({});

  const toggleFieldExpand = (fieldId) => {
    setExpandedFields(prev => ({ ...prev, [fieldId]: !prev[fieldId] }));
  };

  const expandAllFields = () => {
    const allFields = {};
    const labelMap = getActiveFieldLabels();
    Object.keys(labelMap).forEach(key => allFields[key] = true);
    const cfKey = activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType;
    customFields[cfKey]?.fields?.forEach(f => allFields[f.id] = true);
    setExpandedFields(allFields);
  };

  const collapseAllFields = () => {
    setExpandedFields({});
  };

  // Get unified field list (standard + custom)
  const getUnifiedFieldList = () => {
    const cfgKey = activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType;
    const config = formConfigs[cfgKey];
    const customFieldsList = customFields[cfgKey]?.fields || [];
    // Use a path-aware order key for FT
    const orderKey = activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType;
    const savedOrder = unifiedFieldOrder[orderKey];
    
    // Active field-label map (FT has its own per-path field set)
    const labelMap = getActiveFieldLabels();
    
    // Standard fields (filter unsupported per type)
    const standardFieldKeys = Object.keys(labelMap).filter(field => {
      if (activeRequestType !== 'FT') {
        if (field === 'disease_condition' && activeRequestType === 'CTA') return false;
        if (field === 'linked_fea' && activeRequestType === 'FEA') return false;
      }
      return true;
    });
    
    const standardFields = standardFieldKeys.map(key => ({
      id: key,
      label: config?.[key]?.label || labelMap[key].defaultLabel,
      type: 'standard',
      required: config?.[key]?.required,
      enabled: config?.[key]?.enabled !== false,
      isStandard: true
    }));
    
    const customFieldsMapped = customFieldsList.map(f => ({
      ...f,
      isStandard: false
    }));
    
    // If we have a saved order, use it
    if (savedOrder && savedOrder.length > 0) {
      const orderedFields = [];
      const allFieldsMap = {};
      
      // Create a map of all fields
      standardFields.forEach(f => allFieldsMap[f.id] = f);
      customFieldsMapped.forEach(f => allFieldsMap[f.id] = f);
      
      // Add fields in saved order
      savedOrder.forEach(id => {
        if (allFieldsMap[id]) {
          orderedFields.push(allFieldsMap[id]);
          delete allFieldsMap[id];
        }
      });
      
      // Add any new fields that aren't in the saved order
      Object.values(allFieldsMap).forEach(f => orderedFields.push(f));
      
      return orderedFields;
    }
    
    // Default: standard fields first, then custom fields
    return [...standardFields, ...customFieldsMapped];
  };

  const handleUnifiedDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    const fields = getUnifiedFieldList();
    const oldIndex = fields.findIndex(f => f.id === active.id);
    const newIndex = fields.findIndex(f => f.id === over.id);
    
    const newOrder = arrayMove(fields, oldIndex, newIndex).map(f => f.id);
    
    // Use path-aware order key for FT
    const orderKey = activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType;
    
    // Update local state immediately
    setUnifiedFieldOrder(prev => ({
      ...prev,
      [orderKey]: newOrder
    }));
    
    // Save to backend
    try {
      const pathArg = activeRequestType === 'FT' ? ftFormPath : undefined;
      await requestTypeSettingsApi.updateUnifiedFieldOrder(activeRequestType, newOrder, pathArg);
      toast.success('Field order updated');
    } catch (error) {
      toast.error('Failed to update field order');
      // Revert on error
      const pathArg = activeRequestType === 'FT' ? ftFormPath : undefined;
      const res = await requestTypeSettingsApi.getFormConfig(activeRequestType, pathArg);
      setUnifiedFieldOrder(prev => ({
        ...prev,
        [orderKey]: res.data?.unified_field_order || null
      }));
    }
  };

  // Handle drag end for reordering core fields
  const handleCoreFieldDragEnd = async (event, currentFieldKeys) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    const oldIndex = currentFieldKeys.indexOf(active.id);
    const newIndex = currentFieldKeys.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    
    const reorderedCoreKeys = arrayMove(currentFieldKeys, oldIndex, newIndex);
    
    const orderKey2 = activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType;
    const customFieldIds = (customFields[orderKey2]?.fields || []).map(f => f.id);
    const newUnifiedOrder = [...reorderedCoreKeys, ...customFieldIds];
    
    setUnifiedFieldOrder(prev => ({
      ...prev,
      [orderKey2]: newUnifiedOrder
    }));
    
    try {
      const pathArg = activeRequestType === 'FT' ? ftFormPath : undefined;
      await requestTypeSettingsApi.updateUnifiedFieldOrder(activeRequestType, newUnifiedOrder, pathArg);
      toast.success('Core field order updated');
    } catch (error) {
      toast.error('Failed to update field order');
      const res = await requestTypeSettingsApi.getFormConfig(activeRequestType);
      setUnifiedFieldOrder(prev => ({
        ...prev,
        [activeRequestType]: res.data?.unified_field_order || null
      }));
    }
  };

  // Handle drag end for reordering custom fields
  const handleCustomFieldDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    const cfKey = activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType;
    const pathArg = activeRequestType === 'FT' ? ftFormPath : undefined;
    const fields = customFields[cfKey]?.fields || [];
    const oldIndex = fields.findIndex(f => f.id === active.id);
    const newIndex = fields.findIndex(f => f.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    
    const newFields = arrayMove(fields, oldIndex, newIndex);
    const newOrder = newFields.map(f => f.id);
    
    setCustomFields(prev => ({
      ...prev,
      [cfKey]: { ...prev[cfKey], fields: newFields }
    }));
    
    try {
      await requestTypeSettingsApi.updateFieldOrder(activeRequestType, newOrder, pathArg);
      toast.success('Custom field order updated');
    } catch (error) {
      setCustomFields(prev => ({
        ...prev,
        [cfKey]: { ...prev[cfKey], fields }
      }));
      toast.error('Failed to update field order');
    }
  };

  const renderUnifiedFieldOrder = () => {
    const fields = getUnifiedFieldList();
    
    if (fields.length === 0) {
      return <div className="text-slate-500">No fields to display</div>;
    }
    
    return (
      <DndContext 
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleUnifiedDragEnd}
      >
        <SortableContext 
          items={fields.map(f => f.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {fields.map((field, index) => (
              <UnifiedSortableField
                key={field.id}
                field={field}
                index={index}
                isStandard={field.isStandard}
                onEditStandard={(id) => {
                  // Scroll to standard field settings
                  const element = document.querySelector(`[data-testid="form-field-${id}"]`);
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    element.classList.add('ring-2', 'ring-blue-500');
                    setTimeout(() => element.classList.remove('ring-2', 'ring-blue-500'), 2000);
                  }
                }}
                onEditCustom={handleOpenEditCustomField}
                onDeleteCustom={handleDeleteCustomField}
                fieldTypes={CUSTOM_FIELD_TYPES}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    );
  };

  const renderFormConfig = () => {
    const cfgKey = activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType;
    const config = formConfigs[cfgKey];
    if (!config) return <div className="text-slate-500">Loading form configuration...</div>;

    // Filter fields based on request type / FT path
    const labelMap = getActiveFieldLabels();
    const coreFieldKeys = Object.keys(labelMap).filter(field => {
      if (activeRequestType !== 'FT') {
        if (field === 'disease_condition' && activeRequestType === 'CTA') return false;
        if (field === 'linked_fea' && activeRequestType === 'FEA') return false;
      }
      return true;
    });
    
    // Respect saved unified order for core fields (path-aware for FT)
    const orderKey = activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType;
    const savedOrder = unifiedFieldOrder[orderKey];
    let fieldsToShow = coreFieldKeys;
    if (savedOrder && savedOrder.length > 0) {
      const orderedCore = savedOrder.filter(id => coreFieldKeys.includes(id));
      const remaining = coreFieldKeys.filter(id => !orderedCore.includes(id));
      fieldsToShow = [...orderedCore, ...remaining];
    }

    const renderCfgKey = activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType;
    const customFieldsList = customFields[renderCfgKey]?.fields || [];

    // Render a collapsible field card
    const renderFieldCard = (fieldKey, fieldInfo, fieldConfig, isCustom = false, customField = null) => {
      const isExpanded = expandedFields[fieldKey];
      const isAlwaysEnabled = fieldInfo?.alwaysEnabled;
      const isEnabled = isCustom ? customField?.enabled !== false : fieldConfig?.enabled !== false;
      const isRequired = isCustom ? customField?.required : fieldConfig?.required;
      const fieldLabel = isCustom ? customField?.label : (fieldConfig?.label || fieldInfo?.defaultLabel);
      const fieldType = isCustom ? customField?.type : 'standard';

      return (
        <div 
          key={fieldKey}
          className={`border rounded-lg overflow-hidden transition-all ${
            !isEnabled && !isAlwaysEnabled ? 'bg-slate-50 opacity-60' : 'bg-white'
          }`}
          data-testid={`form-field-${fieldKey}`}
        >
          {/* Collapsed Header */}
          <div 
            className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50"
            onClick={() => toggleFieldExpand(fieldKey)}
          >
            <div className="flex items-center gap-3">
              {/* Drag Handle */}
              <div className="text-slate-300 cursor-grab">
                <GripVertical className="w-4 h-4" />
              </div>
              
              {/* Field Icon & Name */}
              <div className="flex items-center gap-2">
                <span className="text-lg">{isCustom ? FIELD_TYPE_ICONS[fieldType] || '📝' : '📄'}</span>
                <span className="font-medium text-slate-900">{fieldLabel}</span>
              </div>
              
              {/* Badges */}
              <div className="flex items-center gap-1.5">
                {isAlwaysEnabled && (
                  <Badge variant="outline" className="text-[10px] bg-slate-100 px-1.5 py-0">
                    <Lock className="w-2.5 h-2.5 mr-0.5" />
                    Core
                  </Badge>
                )}
                {isCustom && (
                  <Badge className="text-[10px] bg-violet-100 text-violet-700 px-1.5 py-0">
                    Custom
                  </Badge>
                )}
                {isRequired && (
                  <Badge className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0">
                    Required
                  </Badge>
                )}
                {!isEnabled && !isAlwaysEnabled && (
                  <Badge className="text-[10px] bg-slate-200 text-slate-500 px-1.5 py-0">
                    Hidden
                  </Badge>
                )}
              </div>
            </div>
            
            {/* Right Side Controls */}
            <div className="flex items-center gap-3">
              {!isAlwaysEnabled && !isCustom && (
                <Switch
                  checked={isEnabled}
                  onCheckedChange={(checked) => {
                    handleFormConfigChange(fieldKey, 'enabled', checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {!isAlwaysEnabled && !isCustom && (
                <button
                  type="button"
                  className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                  title="Hide this field (soft-delete; restore from Hidden Fields below)"
                  onClick={(e) => { e.stopPropagation(); handleFormConfigChange(fieldKey, '_deleted', true); }}
                  data-testid={`hide-core-field-${fieldKey}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
              <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
            </div>
          </div>
          
          {/* Expanded Content */}
          {isExpanded && (
            <div className="px-4 pb-4 pt-2 border-t bg-slate-50/50">
              <p className="text-xs text-slate-500 mb-3">{fieldInfo?.description || customField?.help_text || 'Configure this field'}</p>
              
              {isCustom ? (
                // Custom field expanded view
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Type: <span className="font-medium">{FIELD_TYPE_ICONS[fieldType]} {fieldType}</span></span>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleOpenEditCustomField(customField); }}
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => { e.stopPropagation(); handleDeleteCustomField(customField.id); }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  {customField?.placeholder && (
                    <p className="text-xs text-slate-500">Placeholder: "{customField.placeholder}"</p>
                  )}
                </div>
              ) : (
                // Standard field expanded view
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-600">Custom Label</Label>
                    <Input
                      value={fieldConfig?.label || ''}
                      onChange={(e) => handleFormConfigChange(fieldKey, 'label', e.target.value)}
                      placeholder={fieldInfo?.defaultLabel}
                      className="mt-1 h-8 text-sm"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Placeholder</Label>
                    <Input
                      value={fieldConfig?.placeholder || ''}
                      onChange={(e) => handleFormConfigChange(fieldKey, 'placeholder', e.target.value)}
                      placeholder="Enter placeholder..."
                      className="mt-1 h-8 text-sm"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-600">Help Text</Label>
                    <Input
                      value={fieldConfig?.help_text || ''}
                      onChange={(e) => handleFormConfigChange(fieldKey, 'help_text', e.target.value)}
                      placeholder="Optional help text..."
                      className="mt-1 h-8 text-sm"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="col-span-2 flex items-center gap-4 pt-2">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={fieldConfig?.required === true}
                        onCheckedChange={(checked) => handleFormConfigChange(fieldKey, 'required', checked)}
                        disabled={isAlwaysEnabled && fieldConfig?.required}
                      />
                      <Label className="text-xs text-slate-600">Required field</Label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      );
    };

    // Render Live Preview
    const renderLivePreview = () => {
      const allFields = getUnifiedFieldList();
      const enabledFields = allFields.filter(f => f.enabled);
      
      return (
        <div className="bg-white border rounded-lg p-4 sticky top-4">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b">
            <Eye className="w-4 h-4 text-blue-600" />
            <span className="font-medium text-sm text-slate-700">Live Preview</span>
          </div>
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {enabledFields.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No fields enabled</p>
            ) : (
              enabledFields.map((field, idx) => (
                <div key={field.id} className="space-y-1">
                  <label className="text-xs font-medium text-slate-700 flex items-center gap-1">
                    {field.label}
                    {field.required && <span className="text-red-500">*</span>}
                  </label>
                  {field.isStandard ? (
                    field.id === 'description' || field.id === 'protocol_summary' ? (
                      <div className="h-16 bg-slate-100 rounded border border-slate-200 px-2 py-1 text-xs text-slate-400">
                        {formConfigs[activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType]?.[field.id]?.placeholder || 'Enter text...'}
                      </div>
                    ) : field.id === 'specialties' ? (
                      <div className="h-8 bg-slate-100 rounded border border-slate-200 px-2 py-1 text-xs text-slate-400 flex items-center">
                        Select specialties...
                      </div>
                    ) : field.id === 'cta_display' ? (
                      <div className="bg-emerald-50 rounded border border-emerald-200 px-2 py-1 text-xs text-emerald-700 flex items-center gap-1">
                        🔗 Auto-linked CTA chip (read-only when 1 CTA exists; dropdown when multiple)
                      </div>
                    ) : field.id === 'investigators' ? (
                      <div className="bg-slate-100 rounded border border-slate-200 px-2 py-1 text-xs text-slate-500 flex items-center gap-1">
                        👥 Investigator picker / read-only inherited chips
                      </div>
                    ) : field.id === 'manual_cta_ref_no' || field.id === 'manual_cta_title' ? (
                      <div className="h-8 bg-slate-100 rounded border border-slate-200 px-2 py-1 text-xs text-slate-400 flex items-center">
                        {formConfigs[activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType]?.[field.id]?.placeholder || (field.id === 'manual_cta_ref_no' ? 'e.g., CTA-2024-007' : 'e.g., Drug X Phase III Agreement')}
                      </div>
                    ) : (
                      <div className="h-8 bg-slate-100 rounded border border-slate-200 px-2 py-1 text-xs text-slate-400 flex items-center">
                        {formConfigs[activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType]?.[field.id]?.placeholder || 'Enter value...'}
                      </div>
                    )
                  ) : (
                    <div className="h-8 bg-violet-50 rounded border border-violet-200 px-2 py-1 text-xs text-violet-400 flex items-center">
                      {FIELD_TYPE_ICONS[field.fieldType] || '📝'} {field.fieldType} field
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
          <div className="mt-3 pt-2 border-t">
            <p className="text-[10px] text-slate-400 text-center">
              {enabledFields.length} field{enabledFields.length !== 1 ? 's' : ''} • {enabledFields.filter(f => f.required).length} required
            </p>
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-4">
        {/* Header */}
        <p className="text-sm text-slate-600">
          Configure the {activeRequestType} request form fields. Drag fields to reorder.
        </p>
        
        {/* Two-column layout: Fields + Preview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Field Configuration */}
          <div className="lg:col-span-2 space-y-6">
            {/* UNIFIED Fields List — Core + Custom merged into one drag list */}
            {(() => {
              const visibleCore = fieldsToShow.filter(k => !(config[k]?._deleted));
              const hiddenCore = fieldsToShow.filter(k => config[k]?._deleted);
              // Combined list of {kind, id, ...}
              const customFieldsList = customFields[activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType]?.fields || [];
              // Respect saved unified order if present
              const orderKey = activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType;
              const savedOrder = unifiedFieldOrder[orderKey] || null;
              const allIds = [...visibleCore, ...customFieldsList.map(f => f.id)];
              const orderedIds = savedOrder
                ? [...savedOrder.filter(id => allIds.includes(id)), ...allIds.filter(id => !savedOrder.includes(id))]
                : allIds;
              const customById = Object.fromEntries(customFieldsList.map(f => [f.id, f]));
              
              const handleUnifiedDragEnd = async (event) => {
                const { active, over } = event;
                if (!over || active.id === over.id) return;
                const oldIndex = orderedIds.indexOf(active.id);
                const newIndex = orderedIds.indexOf(over.id);
                if (oldIndex === -1 || newIndex === -1) return;
                const reordered = arrayMove(orderedIds, oldIndex, newIndex);
                setUnifiedFieldOrder(prev => ({ ...prev, [orderKey]: reordered }));
                try {
                  const pathArg = activeRequestType === 'FT' ? ftFormPath : undefined;
                  await requestTypeSettingsApi.updateUnifiedFieldOrder(activeRequestType, reordered, pathArg);
                  toast.success('Field order updated');
                } catch (e) {
                  toast.error('Failed to update field order');
                }
              };
              
              return (
                <>
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-1 h-5 bg-blue-600 rounded"></div>
                        <h3 className="font-semibold text-slate-900">Form Fields <span className="text-slate-400 font-normal text-sm">({orderedIds.length})</span></h3>
                      </div>
                      <Button 
                        onClick={handleOpenAddCustomField}
                        size="sm"
                        className="bg-violet-600 hover:bg-violet-700"
                        data-testid="add-custom-field-btn"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add Custom Field
                      </Button>
                    </div>
                    {orderedIds.length === 0 ? (
                      <div className="text-center py-6 border-2 border-dashed border-slate-200 rounded-lg bg-slate-50/50">
                        <p className="text-sm text-slate-500">No fields yet</p>
                      </div>
                    ) : (
                      <DndContext 
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleUnifiedDragEnd}
                      >
                        <SortableContext 
                          items={orderedIds}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-2">
                            {orderedIds.map((id, index) => {
                              const isCustom = !!customById[id];
                              if (isCustom) {
                                const field = customById[id];
                                return (
                                  <SortableFormCard
                                    key={id}
                                    fieldKey={id}
                                    fieldInfo={null}
                                    fieldConfig={null}
                                    customField={field}
                                    index={index}
                                    isCustom={true}
                                    isExpanded={expandedFields[id]}
                                    onToggleExpand={toggleFieldExpand}
                                    onConfigChange={handleFormConfigChange}
                                    onEditCustomField={handleOpenEditCustomField}
                                    onDeleteCustomField={handleDeleteCustomField}
                                    FIELD_TYPE_ICONS={FIELD_TYPE_ICONS}
                                  />
                                );
                              }
                              const fieldInfo = getActiveFieldLabels()[id] || FORM_FIELD_LABELS[id];
                              if (!fieldInfo) return null;
                              const fieldConfig = config[id] || {};
                              return (
                                <SortableFormCard
                                  key={id}
                                  fieldKey={id}
                                  fieldInfo={fieldInfo}
                                  fieldConfig={fieldConfig}
                                  index={index}
                                  isCustom={false}
                                  isExpanded={expandedFields[id]}
                                  onToggleExpand={toggleFieldExpand}
                                  onConfigChange={handleFormConfigChange}
                                  FIELD_TYPE_ICONS={FIELD_TYPE_ICONS}
                                />
                              );
                            })}
                          </div>
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                  
                  {/* Hidden Core Fields Tray */}
                  {hiddenCore.length > 0 && (
                    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/50 p-4" data-testid="hidden-fields-tray">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-1 h-5 bg-slate-400 rounded"></div>
                        <h3 className="font-medium text-slate-700 text-sm">Hidden Fields <span className="text-slate-400 font-normal">({hiddenCore.length})</span></h3>
                      </div>
                      <p className="text-xs text-slate-500 mb-3">These fields were deleted by an admin and are no longer shown to coordinators. Click Restore to bring one back.</p>
                      <div className="space-y-1.5">
                        {hiddenCore.map(fieldKey => {
                          const fieldInfo = getActiveFieldLabels()[fieldKey] || FORM_FIELD_LABELS[fieldKey];
                          if (!fieldInfo) return null;
                          return (
                            <div key={fieldKey} className="flex items-center justify-between gap-3 p-2 rounded bg-white border border-slate-200" data-testid={`hidden-field-${fieldKey}`}>
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-base">📄</span>
                                <span className="text-sm text-slate-700 truncate">{config[fieldKey]?.label || fieldInfo.defaultLabel}</span>
                              </div>
                              <button
                                type="button"
                                className="text-xs px-2 py-1 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                onClick={() => { handleFormConfigChange(fieldKey, '_deleted', false); toast.success('Field restored.'); }}
                                data-testid={`restore-field-${fieldKey}`}
                              >
                                Restore
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
            
            {/* Save Button */}
            <div className="pt-4 border-t">
              <Button 
                onClick={handleSaveFormConfig} 
                disabled={savingFormConfig}
                className="bg-blue-900 hover:bg-blue-950"
                data-testid="save-form-config-btn"
              >
                {savingFormConfig ? 'Saving...' : 'Save Form Configuration'}
              </Button>
            </div>
          </div>
          
          {/* Right: Live Preview */}
          <div className="lg:col-span-1">
            {renderLivePreview()}
          </div>
        </div>
      </div>
    );
  };

  const formatTemplateKey = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <DashboardLayout title="Configuration">
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-500">Loading configuration...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Configuration">
      <div className="space-y-6" data-testid="admin-config">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 font-['Manrope']">System Configuration</h2>
          <p className="text-slate-500 mt-1">Configure workflow reminders, email templates, and discussion settings</p>
        </div>

        {/* Email Configuration Health & Test */}
        <EmailHealthCard />

        {/* Request Type Tabs */}
        <Tabs value={activeRequestType} onValueChange={setActiveRequestType}>
          <TabsList className="grid w-full max-w-2xl grid-cols-4">
            <TabsTrigger value="FEA" className="gap-2">
              <FileText className="w-4 h-4" />
              FEA (Feasibility)
            </TabsTrigger>
            <TabsTrigger value="CTA" className="gap-2">
              <FileText className="w-4 h-4" />
              CTA (Clinical Trial)
            </TabsTrigger>
            <TabsTrigger value="FT" className="gap-2" data-testid="admin-ft-tab">
              <FileText className="w-4 h-4" />
              FT (Financial)
            </TabsTrigger>
            <TabsTrigger value="IRB" className="gap-2" data-testid="admin-irb-tab">
              <ShieldCheck className="w-4 h-4" />
              IRB
            </TabsTrigger>
          </TabsList>

          <TabsContent value="FEA" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  FEA Configuration
                </CardTitle>
                <CardDescription>
                  Settings for Feasibility Study requests
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" defaultValue={[]} className="w-full">
                  <AccordionItem value="notifications">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Bell className="w-4 h-4 text-slate-600" />
                        <span>Notifications & Reminders</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      {renderUnifiedNotifications()}
                    </AccordionContent>
                  </AccordionItem>
                  
                  <AccordionItem value="discussion">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-slate-600" />
                        <span>Documents & Discussion Settings</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      {renderDiscussionSettings()}
                    </AccordionContent>
                  </AccordionItem>

                  {/* iter167 Issue 35 — Per-study instance cap */}
                  <AccordionItem value="perstudylimit">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-slate-600" />
                        <span>Per-Study Instance Limit</span>
                        <Badge className="ml-2 bg-emerald-100 text-emerald-700">NEW</Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      {renderPerStudyLimit()}
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="uploadtypes">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-600" />
                        <span>Upload Document Types</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      {renderUploadDocTypes()}
                    </AccordionContent>
                  </AccordionItem>
                  
                  <AccordionItem value="formconfig">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Edit className="w-4 h-4 text-slate-600" />
                        <span>Edit Request Form</span>
                        <Badge className="ml-2 bg-violet-100 text-violet-700">NEW</Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      {renderFormConfig()}
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="overviewlayout">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <LayoutGrid className="w-4 h-4 text-slate-600" />
                        <span>Overview Tab Layout</span>
                        <Badge className="ml-2 bg-violet-100 text-violet-700">NEW</Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      <OverviewLayoutEditor requestType={activeRequestType} />
                    </AccordionContent>
                  </AccordionItem>

                  {activeRequestType === 'CTA' && (
                    <AccordionItem value="bundletool">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2">
                          <FileStack className="w-4 h-4 text-slate-600" />
                          <span>Document Bundle Tool</span>
                          <Badge className="ml-2 bg-violet-100 text-violet-700">NEW</Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-4">
                        <BundleToolConfig requestType="CTA" />
                      </AccordionContent>
                    </AccordionItem>
                  )}

                  {activeRequestType === 'CTA' && (
                    <AccordionItem value="signaturerounds">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2">
                          <FileStack className="w-4 h-4 text-slate-600" />
                          <span>E-Signature Rounds</span>
                          <Badge className="ml-2 bg-violet-100 text-violet-700">NEW</Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-4">
                        <SignatureRoundsConfig requestType="CTA" />
                      </AccordionContent>
                    </AccordionItem>
                  )}
                  
                  <AccordionItem value="rolevisibility">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-slate-600" />
                        <span>Role Visibility</span>
                        <Badge className="ml-2 bg-violet-100 text-violet-700">NEW</Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      <RoleVisibilityConfig requestType={activeRequestType} />
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="roledocs">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-600" />
                        <span>Role Document Requirements</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      {renderRoleDocRequirements()}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="CTA" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  CTA Configuration
                </CardTitle>
                <CardDescription>
                  Settings for Clinical Trial Agreement requests
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" defaultValue={[]} className="w-full">
                  <AccordionItem value="notifications">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Bell className="w-4 h-4 text-slate-600" />
                        <span>Notifications & Reminders</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      {renderUnifiedNotifications()}
                    </AccordionContent>
                  </AccordionItem>
                  
                  <AccordionItem value="discussion">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-slate-600" />
                        <span>Documents & Discussion Settings</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      {renderDiscussionSettings()}
                    </AccordionContent>
                  </AccordionItem>

                  {/* iter167 Issue 35 — Per-study instance cap */}
                  <AccordionItem value="perstudylimit">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-slate-600" />
                        <span>Per-Study Instance Limit</span>
                        <Badge className="ml-2 bg-emerald-100 text-emerald-700">NEW</Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      {renderPerStudyLimit()}
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="uploadtypes">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-600" />
                        <span>Upload Document Types</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      {renderUploadDocTypes()}
                    </AccordionContent>
                  </AccordionItem>
                  
                  <AccordionItem value="formconfig">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Edit className="w-4 h-4 text-slate-600" />
                        <span>Edit Request Form</span>
                        <Badge className="ml-2 bg-violet-100 text-violet-700">NEW</Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      {renderFormConfig()}
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="overviewlayout">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <LayoutGrid className="w-4 h-4 text-slate-600" />
                        <span>Overview Tab Layout</span>
                        <Badge className="ml-2 bg-violet-100 text-violet-700">NEW</Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      <OverviewLayoutEditor requestType={activeRequestType} />
                    </AccordionContent>
                  </AccordionItem>

                  {activeRequestType === 'CTA' && (
                    <AccordionItem value="bundletool">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2">
                          <FileStack className="w-4 h-4 text-slate-600" />
                          <span>Document Bundle Tool</span>
                          <Badge className="ml-2 bg-violet-100 text-violet-700">NEW</Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-4">
                        <BundleToolConfig requestType="CTA" />
                      </AccordionContent>
                    </AccordionItem>
                  )}

                  {activeRequestType === 'CTA' && (
                    <AccordionItem value="signaturerounds">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2">
                          <FileStack className="w-4 h-4 text-slate-600" />
                          <span>E-Signature Rounds</span>
                          <Badge className="ml-2 bg-violet-100 text-violet-700">NEW</Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-4">
                        <SignatureRoundsConfig requestType="CTA" />
                      </AccordionContent>
                    </AccordionItem>
                  )}
                  
                  <AccordionItem value="rolevisibility">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-slate-600" />
                        <span>Role Visibility</span>
                        <Badge className="ml-2 bg-violet-100 text-violet-700">NEW</Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      <RoleVisibilityConfig requestType={activeRequestType} />
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="roledocs">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-600" />
                        <span>Role Document Requirements</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      {renderRoleDocRequirements()}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="FT" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  FT Configuration
                </CardTitle>
                <CardDescription>
                  Settings for Financial Transaction requests
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" defaultValue={[]} className="w-full">
                  <AccordionItem value="notifications">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Bell className="w-4 h-4 text-slate-600" />
                        <span>Notifications & Reminders</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      {renderUnifiedNotifications()}
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="discussion">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-slate-600" />
                        <span>Documents & Discussion Settings</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      {renderDiscussionSettings()}
                    </AccordionContent>
                  </AccordionItem>

                  {/* iter167 Issue 35 — Per-study instance cap */}
                  <AccordionItem value="perstudylimit">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-slate-600" />
                        <span>Per-Study Instance Limit</span>
                        <Badge className="ml-2 bg-emerald-100 text-emerald-700">NEW</Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      {renderPerStudyLimit()}
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="uploadtypes">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-600" />
                        <span>Upload Document Types</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      {renderUploadDocTypes()}
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="formconfig">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Edit className="w-4 h-4 text-slate-600" />
                        <span>Edit Request Form</span>
                        <Badge className="ml-2 bg-violet-100 text-violet-700">NEW</Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      {/* Path A/B toggle for FT */}
                      <div className="mb-5 flex items-center gap-2" data-testid="ft-form-path-toggle">
                        <Label className="text-sm text-slate-600 mr-1">Configure for:</Label>
                        <Button
                          type="button"
                          size="sm"
                          variant={ftFormPath === 'A' ? 'default' : 'outline'}
                          className={ftFormPath === 'A' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                          onClick={() => setFtFormPath('A')}
                          data-testid="ft-form-path-a-btn"
                        >
                          Path A — Link to existing Study
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={ftFormPath === 'B' ? 'default' : 'outline'}
                          className={ftFormPath === 'B' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                          onClick={() => setFtFormPath('B')}
                          data-testid="ft-form-path-b-btn"
                        >
                          Path B — Create new Study
                        </Button>
                        <span className="text-[11px] text-slate-400 ml-2">Editing: <span className="font-medium text-slate-600">FT (Path {ftFormPath})</span></span>
                      </div>
                      {renderFormConfig()}
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="overviewlayout">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <LayoutGrid className="w-4 h-4 text-slate-600" />
                        <span>Overview Tab Layout</span>
                        <Badge className="ml-2 bg-violet-100 text-violet-700">NEW</Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      <OverviewLayoutEditor requestType="FT" />
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="bundletool">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <FileStack className="w-4 h-4 text-slate-600" />
                        <span>Document Bundle Tool</span>
                        <Badge className="ml-2 bg-violet-100 text-violet-700">NEW</Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      <BundleToolConfig requestType="FT" />
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="signaturerounds">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <FileStack className="w-4 h-4 text-slate-600" />
                        <span>E-Signature Rounds</span>
                        <Badge className="ml-2 bg-violet-100 text-violet-700">NEW</Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      <SignatureRoundsConfig requestType="FT" />
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="rolevisibility">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-slate-600" />
                        <span>Role Visibility</span>
                        <Badge className="ml-2 bg-violet-100 text-violet-700">NEW</Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      <RoleVisibilityConfig requestType={activeRequestType} />
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="roledocs">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-600" />
                        <span>Role Document Requirements</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4">
                      {renderRoleDocRequirements()}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="IRB" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-yellow-700" />
                  IRB Resources
                </CardTitle>
                <CardDescription>
                  Institution-wide guidance and downloadable templates. Read-only for Company Reps — no submission workflow.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {irbLoading ? (
                  <div className="py-10 flex items-center justify-center text-slate-500 text-sm">Loading…</div>
                ) : (
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="irb-info">Info / Instructions</Label>
                      <Textarea
                        id="irb-info"
                        data-testid="irb-info-textarea"
                        value={irbInfo}
                        onChange={(e) => setIrbInfo(e.target.value)}
                        rows={8}
                        placeholder="Describe your IRB submission process, contact email, useful links, etc."
                      />
                      <div className="flex justify-end">
                        <Button
                          onClick={handleSaveIRBInfo}
                          disabled={irbInfoSaving}
                          data-testid="irb-save-info-btn"
                          className="bg-yellow-600 hover:bg-yellow-700"
                        >
                          {irbInfoSaving ? 'Saving…' : 'Save Info'}
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Downloadable Documents</Label>
                        <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-yellow-600 hover:bg-yellow-700 text-white text-sm cursor-pointer">
                          <Upload className="w-4 h-4" />
                          {irbUploading ? 'Uploading…' : 'Upload File'}
                          <input
                            type="file"
                            className="hidden"
                            onChange={handleUploadIRBDoc}
                            disabled={irbUploading}
                            data-testid="irb-upload-input"
                          />
                        </label>
                      </div>
                      {irbDocs.length === 0 ? (
                        <p className="text-sm text-slate-400 italic py-4">No documents uploaded yet.</p>
                      ) : (
                        <div className="space-y-2">
                          {irbDocs.map((d) => (
                            <IRBDocRow
                              key={d.id}
                              doc={d}
                              onDelete={() => handleDeleteIRBDoc(d.id)}
                              onSaveDescription={(desc) => handleSaveIRBDocDescription(d.id, desc)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Policy Edit Modal */}
        <Dialog open={showPolicyModal} onOpenChange={setShowPolicyModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Reminder Policy</DialogTitle>
              <DialogDescription>
                {selectedPolicy?.label} - {selectedPolicy?.description}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label>Enable Reminders</Label>
                  <p className="text-xs text-slate-500">Turn this reminder on/off</p>
                </div>
                <Switch
                  checked={policyForm.enabled}
                  onCheckedChange={(checked) => setPolicyForm({ ...policyForm, enabled: checked })}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-500" />
                    <Label>Email</Label>
                  </div>
                  <Switch
                    checked={policyForm.send_email}
                    onCheckedChange={(checked) => setPolicyForm({ ...policyForm, send_email: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-slate-500" />
                    <Label>Push</Label>
                  </div>
                  <Switch
                    checked={policyForm.send_push}
                    onCheckedChange={(checked) => setPolicyForm({ ...policyForm, send_push: checked })}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>First Reminder (days)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={policyForm.initial_days}
                    onChange={(e) => setPolicyForm({ ...policyForm, initial_days: parseInt(e.target.value) || 1 })}
                  />
                  <p className="text-xs text-slate-500 mt-1">Days before first reminder</p>
                </div>
                <div>
                  <Label>Frequency (days)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={policyForm.reminder_days}
                    onChange={(e) => setPolicyForm({ ...policyForm, reminder_days: parseInt(e.target.value) || 1 })}
                  />
                  <p className="text-xs text-slate-500 mt-1">Days between reminders</p>
                </div>
                <div>
                  <Label>Max Reminders</Label>
                  <Input
                    type="number"
                    min="1"
                    value={policyForm.max_reminders}
                    onChange={(e) => setPolicyForm({ ...policyForm, max_reminders: parseInt(e.target.value) || 1 })}
                  />
                  <p className="text-xs text-slate-500 mt-1">Stop after N reminders</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label>Enable Escalation</Label>
                    <p className="text-xs text-slate-500">Notify supervisor after max reminders</p>
                  </div>
                  <Switch
                    checked={policyForm.escalation_enabled}
                    onCheckedChange={(checked) => setPolicyForm({ ...policyForm, escalation_enabled: checked })}
                  />
                </div>
                
                {policyForm.escalation_enabled && (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <ArrowUpRight className="w-4 h-4 text-amber-600" />
                    <span className="text-sm text-amber-800">
                      Escalate to <strong>{selectedPolicy?.escalation_role}</strong> after{' '}
                      <Input
                        type="number"
                        min="1"
                        className="w-16 inline-block mx-1 h-7"
                        value={policyForm.escalation_after}
                        onChange={(e) => setPolicyForm({ ...policyForm, escalation_after: parseInt(e.target.value) || 1 })}
                      />{' '}
                      reminders
                    </span>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPolicyModal(false)}>Cancel</Button>
              <Button onClick={handleSavePolicy} className="bg-blue-900 hover:bg-blue-950">
                Save Policy
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Template Edit Modal */}
        <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Email Template</DialogTitle>
              <DialogDescription>
                {selectedTemplate && formatTemplateKey(selectedTemplate.key)}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-500" />
                    <Label>Email Enabled</Label>
                  </div>
                  <Switch
                    checked={templateForm.email_enabled}
                    onCheckedChange={(checked) => setTemplateForm({ ...templateForm, email_enabled: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-slate-500" />
                    <Label>Push Enabled</Label>
                  </div>
                  <Switch
                    checked={templateForm.push_enabled}
                    onCheckedChange={(checked) => setTemplateForm({ ...templateForm, push_enabled: checked })}
                  />
                </div>
              </div>
              
              <div>
                <Label>Subject</Label>
                <Input
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                  placeholder="Email subject line"
                />
              </div>
              <div>
                <Label>Body (HTML)</Label>
                <Textarea
                  value={templateForm.body}
                  onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                  placeholder="Email body in HTML format"
                  rows={15}
                  className="font-mono text-sm"
                />
              </div>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-2">Available Variables:</p>
                <div className="flex flex-wrap gap-2">
                  {['{user_name}', '{request_type}', '{ref_no}', '{title}', '{company_name}', '{specialty}', '{app_url}', '{request_id}'].map(v => (
                    <Badge key={v} variant="outline" className="font-mono text-xs">{v}</Badge>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTemplateModal(false)}>Cancel</Button>
              <Button onClick={handleSaveTemplate} className="bg-blue-900 hover:bg-blue-950">
                Save Template
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Modal */}
        <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
          <DialogContent className="max-w-3xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>Email Preview</DialogTitle>
            </DialogHeader>
            <div className="border rounded-lg p-4 bg-white max-h-[60vh] overflow-auto">
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
          </DialogContent>
        </Dialog>

        {/* Test Email Modal */}
        <Dialog open={showTestEmailModal} onOpenChange={setShowTestEmailModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send Test Email</DialogTitle>
              <DialogDescription>Send a test email to verify SMTP configuration</DialogDescription>
            </DialogHeader>
            <div>
              <Label>Email Address</Label>
              <Input
                type="email"
                value={testEmailAddress}
                onChange={(e) => setTestEmailAddress(e.target.value)}
                placeholder="test@example.com"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTestEmailModal(false)}>Cancel</Button>
              <Button onClick={handleTestEmail} className="bg-blue-900 hover:bg-blue-950">
                <Send size={16} className="mr-2" />
                Send Test
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Custom Field Modal */}
        <Dialog open={showCustomFieldModal} onOpenChange={setShowCustomFieldModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingCustomField ? 'Edit Custom Field' : 'Add Custom Field'}
              </DialogTitle>
              <DialogDescription>
                Configure the field properties for {activeRequestType} requests
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Field Type */}
              <div className="space-y-2">
                <Label>Field Type *</Label>
                <Select 
                  value={customFieldForm.type} 
                  onValueChange={(value) => setCustomFieldForm(prev => ({ ...prev, type: value }))}
                  disabled={!!editingCustomField}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CUSTOM_FIELD_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <span>{type.label}</span>
                          <span className="text-xs text-slate-400">- {type.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Field Label */}
              <div className="space-y-2">
                <Label>Field Label *</Label>
                <Input
                  value={customFieldForm.label}
                  onChange={(e) => setCustomFieldForm(prev => ({ ...prev, label: e.target.value }))}
                  placeholder="Enter field label"
                />
              </div>
              
              {/* Placeholder (for text fields) */}
              {['text', 'textarea'].includes(customFieldForm.type) && (
                <div className="space-y-2">
                  <Label>Placeholder Text</Label>
                  <Input
                    value={customFieldForm.placeholder}
                    onChange={(e) => setCustomFieldForm(prev => ({ ...prev, placeholder: e.target.value }))}
                    placeholder="Enter placeholder text"
                  />
                </div>
              )}
              
              {/* Help Text */}
              <div className="space-y-2">
                <Label>Help Text</Label>
                <Input
                  value={customFieldForm.help_text}
                  onChange={(e) => setCustomFieldForm(prev => ({ ...prev, help_text: e.target.value }))}
                  placeholder="Optional help text shown below the field"
                />
              </div>
              
              {/* Message Content (for message type) */}
              {customFieldForm.type === 'message' && (
                <div className="space-y-2">
                  <Label>Message Content *</Label>
                  <Textarea
                    value={customFieldForm.message_content}
                    onChange={(e) => setCustomFieldForm(prev => ({ ...prev, message_content: e.target.value }))}
                    placeholder="Enter the message/instructions to display"
                    rows={4}
                  />
                </div>
              )}
              
              {/* Options (for select/multiselect) */}
              {['select', 'multiselect'].includes(customFieldForm.type) && (
                <div className="space-y-2">
                  <Label>Options *</Label>
                  <div className="space-y-2">
                    {customFieldForm.options.map((option, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          value={option.label}
                          onChange={(e) => handleUpdateOption(index, 'label', e.target.value)}
                          placeholder="Option label"
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveOption(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddOption}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Option
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Validation for text fields */}
              {['text', 'textarea'].includes(customFieldForm.type) && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Min Length</Label>
                    <Input
                      type="number"
                      value={customFieldForm.validation?.min_length || ''}
                      onChange={(e) => setCustomFieldForm(prev => ({
                        ...prev,
                        validation: { ...prev.validation, min_length: e.target.value ? parseInt(e.target.value) : null }
                      }))}
                      placeholder="No minimum"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Length</Label>
                    <Input
                      type="number"
                      value={customFieldForm.validation?.max_length || ''}
                      onChange={(e) => setCustomFieldForm(prev => ({
                        ...prev,
                        validation: { ...prev.validation, max_length: e.target.value ? parseInt(e.target.value) : null }
                      }))}
                      placeholder="No maximum"
                    />
                  </div>
                </div>
              )}
              
              {/* File Download: Template upload and require download */}
              {customFieldForm.type === 'file_download' && (
                <div className="space-y-4">
                  {/* Template File Upload */}
                  <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg space-y-3">
                    <div className="flex items-center gap-2">
                      <Download className="w-5 h-5 text-amber-600" />
                      <Label className="font-medium text-amber-800">Template File</Label>
                    </div>
                    
                    {/* Show current template if exists */}
                    {(customFieldForm.template_file_name || editingCustomField?.template_file_name) && (
                      <div className="flex items-center justify-between p-2 bg-white rounded border border-amber-200">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-amber-600" />
                          <span className="text-sm font-medium">
                            {customFieldForm.template_file_name || editingCustomField?.template_file_name}
                          </span>
                          <Badge className="bg-emerald-100 text-emerald-700">Uploaded</Badge>
                        </div>
                      </div>
                    )}
                    
                    {/* Upload button - show only after field is saved (has ID) */}
                    {editingCustomField ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="file"
                          id="template-file-input"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              try {
                                const uploadResult = await requestTypeSettingsApi.uploadTemplateFile(activeRequestType, editingCustomField.id, file);
                                toast.success('Template file uploaded successfully');
                                
                                // Refresh custom fields
                                const res = await requestTypeSettingsApi.getCustomFields(activeRequestType);
                                setCustomFields(prev => ({
                                  ...prev,
                                  [activeRequestType]: res.data || { fields: [], field_order: [] }
                                }));
                                
                                // Update the form state AND editingCustomField with the new file info
                                const fileId = uploadResult.data?.file_id;
                                setCustomFieldForm(prev => ({ 
                                  ...prev, 
                                  template_file_id: fileId,
                                  template_file_name: file.name 
                                }));
                                setEditingCustomField(prev => ({
                                  ...prev,
                                  template_file_id: fileId,
                                  template_file_name: file.name
                                }));
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Failed to upload template');
                              }
                            }
                            // Reset the input so the same file can be re-selected
                            e.target.value = '';
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('template-file-input').click()}
                          className="border-amber-300 text-amber-700 hover:bg-amber-100"
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          {(customFieldForm.template_file_name || editingCustomField?.template_file_name) ? 'Replace Template' : 'Upload Template'}
                        </Button>
                        <span className="text-xs text-slate-500">PDF, DOCX, XLSX, etc.</span>
                      </div>
                    ) : (
                      <p className="text-xs text-amber-700">
                        Save the field first, then you can upload the template file.
                      </p>
                    )}
                  </div>
                  
                  {/* Require download toggle */}
                  <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <Switch
                      checked={customFieldForm.require_download}
                      onCheckedChange={(checked) => setCustomFieldForm(prev => ({ ...prev, require_download: checked }))}
                    />
                    <div>
                      <Label className="font-medium">Require Download Before Submit</Label>
                      <p className="text-xs text-slate-500">User must download this file before submitting the request</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* File Upload: Info about where files will appear */}
              {customFieldForm.type === 'file_upload' && (
                <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-800">File Upload Field</p>
                      <p className="text-sm text-blue-700 mt-1">
                        Files uploaded by users through this field will appear in the request's <strong>Documents tab</strong>.
                        The document type will be set to the field's label (e.g., "{customFieldForm.label || 'Field Label'}").
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Conditional Display Logic */}
              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium">Conditional Display</Label>
                    <p className="text-xs text-slate-500">Show this field only when specific conditions are met</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCustomFieldForm(prev => ({
                      ...prev,
                      conditions: [...(prev.conditions || []), { field_id: '', operator: 'equals', value: '' }]
                    }))}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Condition
                  </Button>
                </div>
                
                {customFieldForm.conditions?.length > 0 && (
                  <div className="space-y-2">
                    {customFieldForm.conditions.map((condition, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <Select
                          value={condition.field_id}
                          onValueChange={(val) => {
                            const newConditions = [...customFieldForm.conditions];
                            newConditions[idx] = { ...newConditions[idx], field_id: val };
                            setCustomFieldForm(prev => ({ ...prev, conditions: newConditions }));
                          }}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Select field..." />
                          </SelectTrigger>
                          <SelectContent>
                            {customFields[activeRequestType === 'FT' ? `FT_PATH_${ftFormPath}` : activeRequestType]?.fields
                              ?.filter(f => f.id !== customFieldForm.id)
                              .map(f => (
                                <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        
                        <Select
                          value={condition.operator}
                          onValueChange={(val) => {
                            const newConditions = [...customFieldForm.conditions];
                            newConditions[idx] = { ...newConditions[idx], operator: val };
                            setCustomFieldForm(prev => ({ ...prev, conditions: newConditions }));
                          }}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="equals">equals</SelectItem>
                            <SelectItem value="not_equals">not equals</SelectItem>
                            <SelectItem value="is_empty">is empty</SelectItem>
                            <SelectItem value="is_not_empty">is not empty</SelectItem>
                            <SelectItem value="contains">contains</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        {!['is_empty', 'is_not_empty'].includes(condition.operator) && (
                          <Input
                            value={condition.value || ''}
                            onChange={(e) => {
                              const newConditions = [...customFieldForm.conditions];
                              newConditions[idx] = { ...newConditions[idx], value: e.target.value };
                              setCustomFieldForm(prev => ({ ...prev, conditions: newConditions }));
                            }}
                            placeholder="Value..."
                            className="flex-1"
                          />
                        )}
                        
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-red-500"
                          onClick={() => {
                            const newConditions = customFieldForm.conditions.filter((_, i) => i !== idx);
                            setCustomFieldForm(prev => ({ ...prev, conditions: newConditions }));
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Required & Enabled toggles */}
              <div className="flex items-center gap-6 pt-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={customFieldForm.required}
                    onCheckedChange={(checked) => setCustomFieldForm(prev => ({ ...prev, required: checked }))}
                  />
                  <Label>Required Field</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={customFieldForm.enabled}
                    onCheckedChange={(checked) => setCustomFieldForm(prev => ({ ...prev, enabled: checked }))}
                  />
                  <Label>Enabled</Label>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowCustomFieldModal(false); resetCustomFieldForm(); }}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveCustomField} 
                disabled={savingCustomField}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {savingCustomField ? 'Saving...' : (
                  editingCustomField ? 'Update Field' : (
                    customFieldForm.type === 'file_download' ? 'Save & Continue to Upload' : 'Add Field'
                  )
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
